import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ToastService } from '@sb-core/services/toast.service';
import { asUtc } from '@sb-helpers';
import { TemplateEditorComponent, TemplateEditorExport } from '@sb-shared/components/template-editor/template-editor.component';
import { TinymceConfigHelperService } from '@sb-shared/services/tinymce-config-helper.service';
import { UserService } from '@sb-shared/services/user.service';
import { environment } from 'src/environments/environment';
import type { Editor } from 'tinymce';
import { CommunicationTemplateApi } from './communication-template-api';
import { buildDefaultFooterHtml, buildFooterLogoHtml, FOOTER_LINK_HTML, FooterLogoSize } from './email-footer-markup';
import {
  ALLOWED_IMAGE_UPLOAD_TYPES,
  exceedsMaxImageUploadSize,
  ImageUploadErrorParams,
  imageUploadErrorParams,
  ImageUploadRefusal,
  MAX_IMAGE_UPLOAD_SIZE_MB
} from './image-upload-types';
import { buildUnlayerMergeTags, containsMessageTextTag, normaliseSubject, withoutMessageText } from './merge-tags';
import {
  CommunicationLibraryEntrySummary,
  CommunicationMergeTag,
  EmailFooterCompliance,
  CommunicationTemplate,
  CommunicationTemplateSummary,
  CommunicationTemplateType,
  CommunicationTemplateVersion,
  CommunicationTemplateVersionSummary,
  CommunicationUserTemplateSummary,
  OrganisationEmailReplyToSettings,
  ReplyToMode,
  SaveCommunicationTemplateRequest,
  SaveCommunicationTemplateVersionRequest,
  SaveUserTemplateRequest,
  TestSendRequest,
  UnlayerMergeTags
} from './communication-template.models';

/**
 * Sentinel used to represent the platform Default in the version `<select>` (SB-14889). Real version
 * ids come from an IDENTITY column so they are always >= 1, and `null` already means "no saved version
 * yet" — hence a negative sentinel. It is a *display* value only: {@link
 * CommunicationTemplateManagerComponent.viewingDefault} is what actually gates behaviour, so no code
 * path can mistake the default for a persisted version and write it back over the organisation's own.
 */
const DEFAULT_VERSION_ID = -1;

/**
 * One dot-free run of the characters an email address atom may hold: the RFC 5322 `atext` set, plus any non-ASCII
 * character, which the server's `MailAddress` accepts too (SB-17218).
 */
const TEST_EMAIL_ATOM = "[\\w!#$%&'*+/=?^`{|}~\\u0080-\\uFFFF-]+";

/**
 * A bare address for "Send test": dot-separated atoms either side of the `@`, and no whitespace anywhere (`\s` also
 * covers the non-ASCII spaces the atom class would let in). Like the server, it needs no dot in the domain. With
 * the length check in `testEmailValid` it is stricter than the server, never looser, so the button is not enabled
 * for an address the API then refuses (SB-17218).
 */
const TEST_EMAIL_PATTERN = new RegExp(
  `^(?!.*\\s)${TEST_EMAIL_ATOM}(?:\\.${TEST_EMAIL_ATOM})*@${TEST_EMAIL_ATOM}(?:\\.${TEST_EMAIL_ATOM})*$`
);

/**
 * The longest test-send recipient the server accepts: the width of the recipient column, mirroring
 * `ReplyToValidator.MaxEmailAddressLength` (SB-17218).
 */
const TEST_EMAIL_MAX_LENGTH = 320;

/**
 * The longest subject a custom template may have: the width of `OutGoingCommunication.Subject`, which a send copies it
 * into, mirroring `CommunicationTemplateLimits.MaxEmailSubjectLength`. Counted in UTF-16 code units, as the server,
 * the column and `maxlength` count (SB-17213).
 */
const USER_TEMPLATE_SUBJECT_MAX_LENGTH = 250;

/** How long the footer waits for the school logo to load before inserting it at 250 pixels (SB-13366). */
const FOOTER_LOGO_MEASURE_TIMEOUT_MS = 3000;

/** Factory for the "nothing configured" Reply-To state (SB-15933). */
const ReplyToDefaults = {
  notSet: (): OrganisationEmailReplyToSettings => ({
    replyToMode: ReplyToMode.Inherit,
    defaultReplyToEmailAddress: null,
    defaultReplyToDisplayName: null
  })
};

/**
 * Which left-pane view is on screen. `system` and `custom` pick a template from a list; `footer`
 * (SB-14896) and `replyTo` (SB-15933) are singleton editors for organisation-wide email settings and
 * so replace the right-hand pane wholesale rather than driving a selection.
 */
type TemplateListTab = 'system' | 'custom' | 'footer' | 'replyTo';

/** The template-level settings, which a version switch keeps: the header fingerprint less the subject. */
type TemplateHeader = Pick<
  CommunicationTemplate,
  | 'name'
  | 'replyToMode'
  | 'replyToEmailAddress'
  | 'replyToDisplayName'
  | 'sendToStudents'
  | 'sendToParents'
  | 'sendToStaff'
  | 'isOrganisationFooterIncluded'
  | 'isEnabled'
>;

/** MIME types the email-template image uploader accepts (mirrors the server allow-list, SB-14897). */

/** What a store step tells the editor about the save of one export, once it is over (SB-17105). */
interface CanvasSave {
  landed(): void;
  failed(): void;
}

/**
 * `sb-communication-template-manager` — the shared type-catalogue + Unlayer editor + version manager for
 * a single organisation's communication templates (SB-14943). Hosted by the SysAdmin master-detail screen
 * (which adds an organisation selector) and by the org-level Settings → Emails tab (current organisation).
 * All data access goes through the injected {@link CommunicationTemplateApi}, provided per host, so this
 * component is agnostic of the SysAdmin vs Admin route.
 */
@Component({
  selector: 'sb-communication-template-manager',
  templateUrl: './communication-template-manager.component.html',
  styleUrls: ['./communication-template-manager.component.scss'],
  standalone: false
})
export class CommunicationTemplateManagerComponent implements OnChanges, OnInit {
  /** The organisation whose templates are managed. Changing it reloads the catalogue. */
  @Input() organisationId!: number;

  /**
   * Display name of the owning organisation. Used as the "My templates" section heading so the two
   * left-pane sections read as the template categories (the SchoolsBuddy type catalogue vs the
   * organisation's own templates, SB-14890). Falls back to a generic label when not supplied.
   */
  @Input() organisationName?: string;

  /**
   * Organisation brand colours (hex, with or without a leading `#`) surfaced as presets in the
   * Unlayer colour pickers (SB-14896 B1, branding). Supplied by the host from the edited
   * organisation (NOT the current org — SysAdmin edits others). Absent ⇒ no brand presets.
   */
  @Input() brandDarkColour?: string;
  @Input() brandLightColour?: string;

  /**
   * Where the edited organisation's address is changed, when the signed-in user can open it (SB-17145).
   * The email footer pane's "go to Main Settings" line links there. Main Settings is for an Organisation
   * Super Admin only, and the SysAdmin host edits other organisations, so a host passes it only when the
   * link would work. Absent ⇒ the line is plain text.
   */
  @Input() organisationSettingsRoute?: string | null;

  @ViewChild(TemplateEditorComponent) private editor?: TemplateEditorComponent;
  @ViewChild('previewModal') private previewModal!: TemplateRef<unknown>;
  @ViewChild('createModal') private createModal!: TemplateRef<unknown>;

  // Type catalogue for the organisation.
  types: CommunicationTemplateType[] = [];
  templates: CommunicationTemplateSummary[] = [];
  typeSearch = '';
  selectedTypeId: number | null = null;
  loadingCatalogue = true;

  // User-created templates for the organisation ("My templates", SB-14890).
  userTemplates: CommunicationUserTemplateSummary[] = [];
  selectedUserTemplateId: number | null = null;

  /**
   * What the editor is currently editing: a catalogue type, a user template, or nothing. A user template
   * with no {@link selectedUserTemplateId} is a new one that saving will create.
   */
  selectionKind: 'type' | 'user' | null = null;

  /** True once a template is selected; gates the editor and stays true so Unlayer mounts once (reuse). */
  hasSelection = false;

  /**
   * The type and the custom template last open, so switching list tabs returns to what was open there.
   * Both lists share the one editor, which otherwise kept showing the other list's template.
   */
  private lastTypeId: number | null = null;
  private lastUserTemplateId: number | null = null;

  /**
   * Whether "New template" offers the pre-built gallery (stock templates as a starting point). Hidden for
   * now: product decision of 2026-10-22, system templates are not to be offered as custom ones. While it
   * is off, "New template" opens a blank template straight in the editor instead of the modal below.
   */
  prebuiltTemplatesEnabled = false;

  // "New template" flow (modal) state.
  createName = '';
  createMode: 'blank' | 'prebuilt' = 'blank';
  prebuiltTemplates: CommunicationLibraryEntrySummary[] = [];
  selectedPrebuiltId: number | null = null;
  loadingPrebuilt = false;
  /** Free-text filter over the gallery, matched against name, description and the type's title. */
  prebuiltFilter = '';
  creating = false;

  // Tag editing for the selected user template.
  userTags: string[] = [];
  tagInput = '';
  confirmingDeleteTemplate = false;

  // The selected template in the editor.
  template?: CommunicationTemplate;
  design: object | null = null;
  mergeTags: UnlayerMergeTags = {};
  /** The tags a custom template is offered: all but Message text, which only system templates may use (SB-17215). */
  customTemplateMergeTags: UnlayerMergeTags = {};
  loadingTemplate = false;
  saving = false;
  previewing = false;
  editorReady = false;
  error = false;

  previewSafeHtml: SafeHtml | null = null;

  /**
   * Unrecognised merge tags reported for the previewed HTML, shown as an advisory banner in the
   * preview modal (SB-14895).
   *
   * The server currently always reports none — see {@link CommunicationTemplatePreviewResult.unrecognisedTags}.
   * The banner is wired anyway so the warning comes back with the server change alone.
   */
  previewUnrecognisedTags: string[] = [];

  /** Preview device toggle inside the preview modal (SB-14894 A). */
  previewDevice: 'desktop' | 'mobile' = 'desktop';

  // Test Send (SB-14894 B).
  /** Whether the "Send test" feature is enabled for this environment/region (gate flag). */
  testSendEnabled = false;
  /** Recipient address for a test send (prefilled with the current user's email, editable). */
  /**
   * Recipient for "Send test", pre-filled with the signed-in user's own address — testing a template
   * on yourself is the overwhelmingly common case, and an empty box made every test a retype. Still
   * freely editable, and left blank if the address cannot be resolved.
   */
  testEmail = '';
  /** True while a test send is in flight. */
  sendingTest = false;
  /** What the editor last said through canvasDirtyChange; see {@link dirty}. */
  private canvasDirty = false;

  /**
   * True when the canvas has unsaved edits; one of the inputs to {@link hasUnsavedTemplateChanges} (and so to
   * {@link testSendNeedsSave}). The editor decides it and publishes it (SB-17105): read-only here, and written by
   * {@link onCanvasDirtyChange} only.
   */
  get dirty(): boolean {
    return this.canvasDirty;
  }

  /**
   * Fingerprint of the header fields as last loaded or saved, or null when unknown (nothing selected,
   * or a load in flight). Compared against the live values to tell whether anything changed.
   *
   * `dirty` alone cannot answer that: it follows the canvas only, as the editor reports it, so editing the
   * subject, the reply-to or any of the flags leaves it false — which is why Save used to look the same
   * whether or not the author had touched anything, and why Test Send could dispatch a stale subject.
   */
  private metaBaseline: string | null = null;

  /**
   * The header fields as last loaded or saved: what a confirmed discard puts back when the template itself
   * stays open (another version, a copy of the Default). Null whenever {@link metaBaseline} is.
   */
  private savedHeader: TemplateHeader | null = null;

  // Versions of the selected template (newest first); the active one is loaded into the editor by default.
  versions: CommunicationTemplateVersionSummary[] = [];
  selectedVersionId: number | null = null;
  loadingVersions = false;
  confirmingDeleteVersionId: number | null = null;

  /**
   * The version whose name is being edited, or null (SB-17138). Captured when the rename starts, so the name
   * lands on that version even if the selection moves; the other version actions are hidden meanwhile.
   */
  renamingVersionId: number | null = null;
  /** The name being typed for {@link renamingVersionId}. Blank clears the name back to "Version N". */
  versionNameDraft = '';

  // ---- Platform Default, shown read-only alongside the organisation's own versions (SB-14889) ----

  /**
   * True while the read-only Default is on screen. This — not {@link selectedVersionId} — is the single
   * gate for the whole mode: it hides every write action and short-circuits {@link onHtmlExport}. The
   * Unlayer canvas keeps the author's working version mounted underneath, so viewing the default is
   * lossless (unsaved edits survive) and returning is instant.
   */
  viewingDefault = false;

  /** The platform default for the selected type, fetched with the template. `null` ⇒ no default exists. */
  defaultTemplate: CommunicationTemplate | null = null;

  /** Server-rendered HTML of {@link defaultTemplate}, shown in a sandboxed iframe. */
  defaultSafeHtml: SafeHtml | null = null;

  /**
   * Unrecognised merge tags reported for the platform Default, shown as an advisory banner above the
   * read-only pane (SB-14895). Worth surfacing separately from {@link previewUnrecognisedTags}: a
   * default carrying a stale token is the one case the author cannot fix in place, and copying it
   * would carry the problem into their own version.
   *
   * Same caveat as {@link previewUnrecognisedTags} — the server currently always reports none.
   */
  defaultUnrecognisedTags: string[] = [];

  /** Version selected before peeking at the default, so exiting restores it without a refetch. */
  private versionBeforeDefault: number | null = null;

  loadingDefault = false;
  defaultRenderError = false;
  copyingFromDefault = false;
  confirmingCopyDiscard = false;

  /** Device toggle for the read-only Default pane (mirrors the preview modal's, SB-14894 A). */
  defaultPreviewDevice: 'desktop' | 'mobile' = 'desktop';

  /** True while an activate/delete version mutation is in flight (locks the version dropdown). */
  mutatingVersion = false;

  // ---- Organisation email footer (SB-14896) ----

  /** The footer in the editor. */
  footerHtml = '';

  /** The last saved footer, as normalised by TinyMCE — the baseline `footerDirty` compares against. */
  footerHtmlClean = '';

  /** Unsaved footer edits. Deliberately separate from {@link dirty}, which follows the template canvas and
   *  is cleared by template-lifecycle steps (including `resetSelection`) that have nothing to do with the
   *  footer, and which also gates Test Send. */
  footerDirty = false;

  footerLoading = true;

  footerSaving = false;

  /** Set when the last save stored something different from what was submitted (see {@link saveFooter}). */
  footerRewritten = false;

  /** Lazily set the first time the footer tab is opened; gates mounting its editor. */
  footerTabVisited = false;

  /** The read-only compliance block for this organisation, or null while loading / on failure. */
  footerCompliance: EmailFooterCompliance | null = null;

  /**
   * Whether the compliance request for this organisation has answered, either way. "Set to default"
   * waits for it: before then a default would silently leave out the school's logo.
   */
  footerComplianceSettled = false;

  /**
   * The footer's TinyMCE instance, handed over by `(onInit)` (SB-13366). Not `editor`: that name is the
   * Unlayer template editor. Null until the editor exists, and again after an organisation change,
   * which destroys and recreates it.
   */
  private footerEditor: Editor | null = null;

  /**
   * Set once the author clicks or types in the footer editor. Until then — and again after the whole
   * content is replaced — a quick button adds its block at the end, because TinyMCE's own caret then
   * sits at the very top.
   */
  private footerCaretPlaced = false;

  /** The logo's natural size, measured once per organisation; null until known or when it cannot be. */
  private footerLogoSize: FooterLogoSize | null = null;

  /** Whether measuring the logo has finished for this organisation, whatever it found. */
  private footerLogoMeasured = false;

  /**
   * Incremented only by {@link resetFooter}, i.e. once per organisation change. Footer loads and saves
   * capture it and drop their response if it has moved on. Not {@link loadToken}: that one also moves on
   * every template selection, which would throw away the footer responses of the organisation still
   * on screen.
   */
  private footerLoadToken = 0;

  // ---- Organisation Reply-To default (SB-15933) ----

  /** Exposed for the template's mode radios; `ReplyToMode` is not otherwise visible from HTML. */
  readonly replyToModes = ReplyToMode;

  /**
   * The Reply-To default being edited. Never null — an organisation with no stored row edits the
   * "not set" state, which is exactly `Inherit` with an empty pair.
   */
  replyTo: OrganisationEmailReplyToSettings = ReplyToDefaults.notSet();

  /** The last saved value, as normalised by the server — the baseline {@link replyToDirty} compares against. */
  private replyToClean: OrganisationEmailReplyToSettings = ReplyToDefaults.notSet();

  /** Unsaved Reply-To edits. Separate from {@link dirty} for the same reason {@link footerDirty} is. */
  replyToDirty = false;

  replyToLoading = true;

  replyToSaving = false;

  private pendingAction: 'save' | 'preview' | 'saveAsNew' | 'testSend' = 'save';

  /**
   * Incremented on every selection change. Loads capture it and drop their response if it has moved
   * on, so a slow request for the previously selected template can never write its content over the
   * current one. Nothing here cancels in-flight HTTP, and responses arrive in completion order rather
   * than request order — without this, switching types while a load is in flight can leave `template`
   * / `defaultTemplate` describing one type while `selectedTypeId` says another, and the next save
   * would persist the mismatch.
   */
  private loadToken = 0;

  constructor(
    private api: CommunicationTemplateApi,
    private sanitizer: DomSanitizer,
    private modal: NgbModal,
    private translate: TranslateService,
    private tinymceConfigHelper: TinymceConfigHelperService,
    private userService: UserService,
    private toast: ToastService
  ) {}

  /**
   * Unsaved work anywhere in this component — read by both hosts' navigation guards.
   *
   * `dirty` alone is not enough now that the footer is here, and the footer cannot reuse it: `dirty`
   * is reset by template-selection lifecycle the footer must survive.
   */
  get hasUnsavedWork(): boolean {
    return this.hasUnsavedTemplateChanges || this.footerDirty || this.replyToDirty;
  }

  /**
   * TinyMCE options for the footer editor.
   *
   * `forceLinkPrefix: false` is mandatory: every real stored footer targets its link with a merge
   * tag (`href="~~{SITEURL}~~"`), and the helper's default rewrites any non-URL href to
   * `http://…` on every BeforeSetContent — corrupting the tag on load, not just on paste.
   *
   * Uploads go through this component's own {@link imageUploader}, which is scoped by the
   * `organisationId` input. The messaging editor's uploader reads
   * `window.EveryBuddy.CurrentOrganisationId` instead, which in the SysAdmin host is the SysAdmin's
   * own organisation — wrong tenant, and a 403 besides, since the storage endpoint wants an
   * org-level role in the target organisation. {@link uploadFooterImage} wraps it to translate a refusal,
   * which TinyMCE would show as a raw label tag (SB-17178).
   *
   * The entity settings (SB-13366) make TinyMCE write accented and other non-ASCII characters as they
   * are, as the server's sanitiser stores them. With TinyMCE's default named entities, `é` went out as
   * `&eacute;`, came back raw, and every save of a footer naming "Lycée…" said it had been cleaned up.
   * The helper builds a fixed object, so they are spread over its result.
   */
  get footerTinymceOptions(): Record<string, unknown> {
    return {
      ...(this.tinymceConfigHelper.getTinyMceConfig({
        forceLinkPrefix: false,
        images_upload_handler: (blobInfo: { blob: () => Blob; filename: () => string }) =>
          this.uploadFooterImage(blobInfo)
      } as never) as Record<string, unknown>),
      entity_encoding: 'named',
      entities: '160,nbsp'
    };
  }

  /**
   * Stores an image put into the footer through {@link imageUploader}, and says in words why one was refused.
   * TinyMCE shows a rejection's text as it is, in its Insert/edit image alert or its "Failed to upload image"
   * notification, and cannot translate a label tag; only the template editor's alert above the canvas did
   * (SB-17178). So the tag is translated here, with the values its text interpolates, which the refusal carries.
   * `remove` takes a refused pasted or dropped image out of the footer rather than leaving it there to be saved as
   * a data: URI. This never sets {@link imageError}, so no alert shows above the template editor, though
   * imageUploader still clears one that is showing.
   */
  private uploadFooterImage(blobInfo: { blob: () => Blob; filename: () => string }): Promise<string> {
    const blob = blobInfo.blob();
    const upload = this.imageUploader(new File([blob], blobInfo.filename(), { type: blob.type }));
    return upload.catch((refusal: ImageUploadRefusal) =>
      Promise.reject({ message: this.translate.instant(refusal.message, refusal.params), remove: true })
    );
  }

  /**
   * Unlayer options seeding the colour pickers with the organisation's brand colours (SB-14896 B1).
   * Spread verbatim by `sb-template-editor`; returns `{}` (no-op) when no brand colours are supplied.
   * Colours are stored without a leading `#` (DB) so one is prefixed when missing.
   */
  get editorBrandOptions(): Record<string, unknown> {
    const presets = [this.brandDarkColour, this.brandLightColour]
      .filter((colour): colour is string => !!colour && colour.trim().length > 0)
      .map((colour) => (colour.startsWith('#') ? colour : `#${colour}`));

    return presets.length > 0
      ? { features: { colorPicker: { presets } } }
      : {};
  }

  ngOnInit(): void {
    // Independent of organisationId, so it belongs here and not in ngOnChanges: the signed-in user
    // does not change when a SysAdmin switches which organisation they are editing.
    this.userService.getCurrentUser().subscribe({
      next: (user) => {
        if (!this.testEmail) {
          this.testEmail = user?.email ?? '';
        }
      },
      error: () => {
        // A missing address is not worth failing the screen over — the field just stays empty.
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organisationId'] && this.organisationId != null) {
      this.resetSelection();
      this.lastTypeId = null;
      this.lastUserTemplateId = null;
      this.resetFooter();
      this.resetReplyTo();
      // In the SysAdmin host the Custom tab can be open when the organisation changes: it opens on a blank
      // template there as well, once the new organisation's list is in.
      this.loadCatalogue(() => this.openNewUserTemplateIfIdle());
      this.loadTestSendEnabled();
      this.loadFooter();
      this.loadFooterCompliance();
      this.loadReplyTo();
    }
  }

  // ---- Organisation email footer (SB-14896) ----

  /**
   * Clears footer state for a new organisation.
   *
   * The SysAdmin host does not remount this component when the selected organisation changes — its
   * `*ngIf` stays truthy, so only the `@Input` updates. Without this the footer pane would keep
   * showing the previous organisation's HTML with Save still enabled, and the next Save would write
   * organisation A's footer onto organisation B. That bug is invisible in the Settings host, which
   * has no organisation selector, so nothing there would ever surface it.
   */
  private resetFooter(): void {
    this.footerLoadToken++;
    this.footerHtml = '';
    this.footerHtmlClean = '';
    this.footerDirty = false;
    this.footerCompliance = null;
    this.footerComplianceSettled = false;
    this.footerLoading = true;

    // A save still in flight belongs to the previous organisation; its response is dropped (see
    // saveFooter), so these would otherwise stay set and keep the new organisation's Save disabled.
    this.footerSaving = false;
    this.footerRewritten = false;

    // The footer editor is destroyed with the loading state and recreated for this organisation.
    this.footerEditor = null;
    this.footerCaretPlaced = false;
    this.footerLogoSize = null;
    this.footerLogoMeasured = false;
  }

  private loadFooter(): void {
    // Same stale-response guard as template loads: responses arrive in completion order, so a slow
    // GET for the previously selected organisation must not repopulate the pane. resetFooter() has
    // already incremented footerLoadToken for this change.
    const token = this.footerLoadToken;

    this.api
      .getEmailFooter(this.organisationId)
      .pipe(catchError(() => of(null)))
      .subscribe((footer) => {
        if (token !== this.footerLoadToken) {
          return;
        }

        this.footerHtmlClean = footer?.htmlFooterTemplate ?? '';
        this.footerHtml = this.footerHtmlClean;
        this.footerDirty = false;
        this.footerLoading = false;
      });
  }

  private loadFooterCompliance(): void {
    const token = this.footerLoadToken;

    this.api
      .getEmailFooterCompliance(this.organisationId)
      .pipe(catchError(() => of(null)))
      .subscribe((compliance) => {
        if (token !== this.footerLoadToken) {
          return;
        }

        this.footerCompliance = compliance;
        this.footerComplianceSettled = true;
        this.measureFooterLogo(compliance?.logoUrl, token);
      });
  }

  /**
   * tinymce-angular pushes the editor's *normalised* content back into the model on init when it
   * differs from the input — and TinyMCE does normalise (`<img … />` becomes `<img …>`, which every
   * real stored footer contains). Adopting that as the clean baseline is what stops the pane
   * reporting itself dirty before the user types. Never compare the editor's value against the
   * string the API returned.
   *
   * The editor itself is kept for the quick buttons (SB-13366), with two listeners for where they
   * insert: a click or key press means the author has placed the caret; replacing the whole content
   * (Set to default, Undo changes, undo/redo) puts it back at the top, so the next insert goes at the
   * end again.
   */
  onFooterEditorReady(editor?: Editor): void {
    this.footerHtmlClean = this.footerHtml ?? '';
    this.footerDirty = false;

    if (editor) {
      this.footerEditor = editor;
      this.footerCaretPlaced = false;
      editor.on('mousedown keydown', () => (this.footerCaretPlaced = true));
      editor.on('SetContent', (event: { selection?: boolean }) => {
        if (!event.selection) {
          this.footerCaretPlaced = false;
        }
      });
    }
  }

  onFooterChanged(): void {
    this.footerDirty = (this.footerHtml ?? '') !== this.footerHtmlClean;
  }

  /**
   * The compliance block's address and unsubscribe HTML, trusted for rendering.
   *
   * Safe to bypass: both are produced server-side by the same Core code the send path uses, not by
   * any author. The organisation's address fields are admin-supplied but `CanSpamAddressFormatter`
   * HTML-encodes every part before it reaches here.
   */
  get complianceAddressSafeHtml(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.footerCompliance?.addressHtml ?? '');
  }

  get complianceUnsubscribeSafeHtml(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.footerCompliance?.unsubscribeHtml ?? '');
  }

  /** True when the footer has unsaved edits and is safe to save. */
  get canSaveFooter(): boolean {
    return !this.footerLoading && !this.footerSaving && this.footerDirty;
  }

  /** Reverts the editor to the last saved value (the legacy pane's "Undo changes"). */
  undoFooterChanges(): void {
    this.replaceFooterEditorContent(this.footerHtmlClean);
    this.footerHtml = this.footerHtmlClean;
    this.footerDirty = false;
  }

  /**
   * Puts `html` in the footer editor as an undo level of its own (SB-13366).
   *
   * Assigning `footerHtml` alone reaches TinyMCE as `setContent`, which records no undo level, so the
   * editor's last level would still hold the content just replaced. TinyMCE fires `change` only when new
   * content differs from that last level: a quick button that then recreated it ("Set to default", "Undo
   * changes", "Set to default") would change the editor but not the model, and Save would stay disabled.
   */
  private replaceFooterEditorContent(html: string): void {
    const editor = this.footerEditor;
    if (editor && !editor.removed) {
      editor.undoManager.transact(() => editor.setContent(html));
    }
  }

  saveFooter(): void {
    if (!this.canSaveFooter) {
      return;
    }

    const submitted = this.footerHtml ?? '';
    const token = this.footerLoadToken;
    this.footerSaving = true;
    this.footerRewritten = false;

    this.api.saveEmailFooter(this.organisationId, submitted).subscribe({
      next: (saved) => {
        // The organisation changed while this save was in flight. Adopting its response would put the
        // previous organisation's footer in this pane as the clean value, and the next Save would
        // write it to the organisation now on screen.
        if (token !== this.footerLoadToken) {
          return;
        }

        const stored = saved?.htmlFooterTemplate ?? '';

        // Adopt what was actually stored, and say so when it differs. Saving sanitises, and the
        // sanitiser cannot keep comments selectively — so a footer carrying Outlook conditional
        // fallbacks comes back rewritten. Silently leaving the editor showing the submitted value
        // would hide that from the only person able to react to it.
        this.footerRewritten = stored !== submitted;
        this.footerHtmlClean = stored;
        if (this.footerRewritten) {
          this.replaceFooterEditorContent(stored);
        }
        this.footerHtml = stored;
        this.footerDirty = false;
        this.footerSaving = false;
      },
      error: () => {
        if (token === this.footerLoadToken) {
          this.footerSaving = false;
        }
      }
    });
  }

  // ---- Footer quick buttons (SB-13366) ----

  /** The buttons need the footer's editor, and nothing loading or saving underneath them. */
  get canInsertIntoFooter(): boolean {
    return !!this.footerEditor && !this.footerEditor.removed && !this.footerLoading && !this.footerSaving;
  }

  /** Waits for the logo's size too: inserted before it is known, a small logo would be drawn 250 pixels wide. */
  get canInsertFooterLogo(): boolean {
    return this.canInsertIntoFooter && !!this.footerCompliance?.logoUrl && this.footerLogoMeasured;
  }

  /**
   * Waits for the compliance answer, which carries the logo (a default built before it would lack one),
   * and for the logo's size when there is a logo.
   */
  get canSetFooterToDefault(): boolean {
    return (
      this.canInsertIntoFooter &&
      this.footerComplianceSettled &&
      (!this.footerCompliance?.logoUrl || this.footerLogoMeasured)
    );
  }

  /**
   * The school has no logo: the server said so with an explicit null. A failed request, or a server
   * from before the field existed, says nothing either way, so no hint then.
   */
  get showFooterNoLogoHint(): boolean {
    return this.footerCompliance?.logoUrl === null;
  }

  /** Criterion 6a: puts the centred "Go to SchoolsBuddy Website" link in. */
  insertFooterSiteLink(): void {
    this.insertFooterBlock(FOOTER_LINK_HTML);
  }

  /** Criterion 6b: puts the school logo in, centred and at most 250 pixels wide. */
  insertFooterLogo(): void {
    const logoUrl = this.footerCompliance?.logoUrl;
    if (!this.canInsertFooterLogo || !logoUrl) {
      return;
    }

    this.insertFooterBlock(buildFooterLogoHtml(logoUrl, this.footerOrganisationName, this.footerLogoSize));
  }

  /**
   * Criterion 6c: replaces the editor's content with the SchoolsBuddy default, the site link and the logo.
   *
   * Through TinyMCE's own command, not by assigning `footerHtml`: the command records an undo level and
   * fires `change`, so Ctrl+Z gets the previous content back and the model and dirty flag follow by
   * themselves. An assignment would do neither. `footerHtmlClean` is left alone, so "Undo changes"
   * still returns to the last saved footer. Nothing is saved until Save.
   */
  setFooterToDefault(): void {
    const editor = this.footerEditor;
    if (!this.canSetFooterToDefault || !editor) {
      return;
    }

    const logoUrl = this.footerCompliance?.logoUrl;
    const html = buildDefaultFooterHtml(
      this.footerOrganisationName,
      logoUrl ? { url: logoUrl, size: this.footerLogoSize } : null
    );

    editor.execCommand('mceSetContent', false, html, { skip_focus: true });
  }

  /**
   * The school's display name, the inserted logo's alternative text. A logo comes only with the
   * compliance answer, which is read fresh for the organisation being edited; the input covers an answer
   * that has a logo but no name.
   */
  private get footerOrganisationName(): string {
    return (this.footerCompliance?.organisationName || this.organisationName || '').trim();
  }

  /**
   * Adds one block on its own line: after the paragraph, list or table holding the caret, or at the end
   * when the author has not placed the caret. An empty block there (an empty editor, or the blank line
   * the caret sits on) is replaced.
   *
   * Next to the block rather than at the caret. TinyMCE's `insertContent` first focuses the editor and
   * moves the caret into neighbouring text; when that text is a link, the inserted link loses its
   * `href` and the old link is split. Inserting a fragment next to a block avoids the caret entirely.
   * `transact` records it as one undo level and fires `change`, which updates the model.
   *
   * `html` must come from the `email-footer-markup.ts` builders, which escape every value they put in.
   * `createFragment` parses it straight into the editor's document, past TinyMCE's own parser and its
   * XSS sanitising, so a school name inserted raw could run script in the admin's session.
   */
  private insertFooterBlock(html: string): void {
    const editor = this.footerEditor;
    if (!this.canInsertIntoFooter || !editor) {
      return;
    }

    const body = editor.getBody();
    const anchor = this.footerInsertAnchor(editor, body);

    editor.undoManager.transact(() => {
      const fragment = editor.dom.createFragment(html);
      // Read before inserting: a fragment empties as its nodes move into the document.
      const inserted = fragment.lastChild;

      if (anchor) {
        editor.dom.insertAfter(fragment, anchor);
        if (this.isBlankFooterLine(editor, anchor)) {
          editor.dom.remove(anchor);
        }
      } else {
        body.appendChild(fragment);
      }

      // At the end of the new block but outside any link in it, so a second press lands after this
      // block, and typing or the toolbar's link button does not edit the SchoolsBuddy link.
      if (inserted) {
        editor.selection.setCursorLocation(inserted, inserted.childNodes.length);
      }
    });
  }

  /**
   * An empty line: a paragraph-like block with no text (spaces and non-breaking spaces aside) and
   * nothing in it but line breaks. Not TinyMCE's `dom.isEmpty`, which also counts an `<hr>` as empty and
   * does not count a stored `<p>&nbsp;</p>`.
   */
  private isBlankFooterLine(editor: Editor, block: Element): boolean {
    return (
      !!editor.schema.getTextBlockElements()[block.nodeName] &&
      (block.textContent ?? '').replace(/[\s\u00a0\ufeff]/g, '') === '' &&
      Array.from(block.querySelectorAll('*')).every(child => child.nodeName === 'BR')
    );
  }

  /** The top-level block the next insert goes after, or null for an editor with no blocks at all. */
  private footerInsertAnchor(editor: Editor, body: HTMLElement): Element | null {
    if (this.footerCaretPlaced) {
      let node: Node | null = editor.selection.getEnd();
      while (node && node.parentNode && node.parentNode !== body) {
        node = node.parentNode;
      }

      if (node && node.parentNode === body && node.nodeType === Node.ELEMENT_NODE) {
        return node as Element;
      }
    }

    return body.lastElementChild;
  }

  /**
   * Measures the logo once per organisation, so the inserted image is never drawn wider than it is.
   * A size that arrives after an organisation change is dropped.
   */
  private measureFooterLogo(logoUrl: string | null | undefined, token: number): void {
    this.footerLogoSize = null;
    this.footerLogoMeasured = !logoUrl;
    if (!logoUrl) {
      return;
    }

    this.loadFooterLogoSize(logoUrl).then(size => {
      if (token === this.footerLoadToken) {
        this.footerLogoSize = size;
        this.footerLogoMeasured = true;
      }
    });
  }

  /**
   * Loads the image off-screen for its natural size; null when it cannot be loaded, or has not loaded
   * within a few seconds, so a stalled CDN cannot keep the logo buttons disabled. A seam for tests.
   */
  protected loadFooterLogoSize(logoUrl: string): Promise<FooterLogoSize | null> {
    return new Promise(resolve => {
      const image = new Image();
      const timeout = setTimeout(() => resolve(null), FOOTER_LOGO_MEASURE_TIMEOUT_MS);
      image.onload = () => {
        clearTimeout(timeout);
        resolve(
          image.naturalWidth > 0 && image.naturalHeight > 0
            ? { width: image.naturalWidth, height: image.naturalHeight }
            : null
        );
      };
      image.onerror = () => {
        clearTimeout(timeout);
        resolve(null);
      };
      image.src = logoUrl;
    });
  }

  // ---- Organisation Reply-To default (SB-15933) ----

  /**
   * Clears Reply-To state for a new organisation — the same protection {@link resetFooter} exists for.
   * The SysAdmin host keeps this component mounted across organisation changes, so without this the
   * panel would keep showing organisation A's address with Save enabled, and the next Save would write
   * it onto organisation B.
   */
  private resetReplyTo(): void {
    this.replyTo = ReplyToDefaults.notSet();
    this.replyToClean = ReplyToDefaults.notSet();
    this.replyToDirty = false;
    this.replyToLoading = true;
  }

  private loadReplyTo(): void {
    // Same stale-response guard as the footer and template loads: a slow GET for the previously
    // selected organisation must not repopulate the panel.
    const token = this.loadToken;

    this.api
      .getReplyToSettings(this.organisationId)
      .pipe(catchError(() => of(null)))
      .subscribe((settings) => {
        if (token !== this.loadToken) {
          return;
        }

        // A null body is the normal starting state, not a failure: it means "not set".
        this.replyToClean = settings ? { ...settings } : ReplyToDefaults.notSet();
        this.replyTo = { ...this.replyToClean };
        this.replyToDirty = false;
        this.replyToLoading = false;
      });
  }

  /**
   * Switches mode from the radios, clearing the address pair on the way out of Custom.
   *
   * Clearing matters: the server drops the pair for any non-Custom mode, so leaving it on screen would
   * show an address that is about to be discarded — and coming back to Custom would silently re-submit
   * it. Better that the fields are visibly empty.
   */
  onReplyToModeChange(mode: ReplyToMode): void {
    this.replyTo.replyToMode = mode;

    if (mode !== ReplyToMode.Custom) {
      this.replyTo.defaultReplyToEmailAddress = null;
      this.replyTo.defaultReplyToDisplayName = null;
    }

    this.onReplyToChanged();
  }

  /**
   * The checkbox that opens the organisation's Reply-To choice — the same shape as the per-template
   * override's, and for the same reason: "not set" is the state most organisations stay in, and as a
   * third item in a flat list it read as a decision every school had to take.
   *
   * On starts from a custom address (Save stays blocked until one is typed, as in the template block);
   * off returns to the default and clears the pair, since the server drops it for any non-Custom mode.
   */
  onSetReplyToChange(event: Event): void {
    const on = (event.target as HTMLInputElement).checked;

    this.onReplyToModeChange(on ? ReplyToMode.Custom : ReplyToMode.Inherit);
  }

  onReplyToChanged(): void {
    this.replyToDirty =
      this.replyTo.replyToMode !== this.replyToClean.replyToMode ||
      this.normalisedReplyToAddress !== (this.replyToClean.defaultReplyToEmailAddress ?? '') ||
      (this.replyTo.defaultReplyToDisplayName ?? '').trim() !== (this.replyToClean.defaultReplyToDisplayName ?? '');
  }

  /** True while the Custom mode is selected but has no usable address — the one blocking state. */
  get replyToAddressMissing(): boolean {
    return this.replyTo.replyToMode === ReplyToMode.Custom && this.normalisedReplyToAddress.length === 0;
  }

  /**
   * The guidance line for the selected mode. Rendered under the radios so the consequence of the
   * choice is on screen before anything is saved — the question the ticket actually asks.
   */
  get replyToGuidanceLabel(): string {
    switch (this.replyTo.replyToMode) {
      case ReplyToMode.Custom:
        return 'SB_Reply_To_Custom_Guidance';
      case ReplyToMode.NoReply:
        return 'SB_Reply_To_No_Reply_Guidance';
      default:
        return 'SB_Reply_To_Not_Set_Guidance';
    }
  }

  /** True when the Reply-To panel has valid unsaved edits. */
  get canSaveReplyTo(): boolean {
    return !this.replyToLoading && !this.replyToSaving && this.replyToDirty && !this.replyToAddressMissing;
  }

  /** Reverts the panel to the last saved value. */
  undoReplyToChanges(): void {
    this.replyTo = { ...this.replyToClean };
    this.replyToDirty = false;
  }

  saveReplyTo(): void {
    if (!this.canSaveReplyTo) {
      return;
    }

    this.replyToSaving = true;

    this.api
      .saveReplyToSettings(this.organisationId, {
        replyToMode: this.replyTo.replyToMode,
        defaultReplyToEmailAddress: this.normalisedReplyToAddress || null,
        defaultReplyToDisplayName: (this.replyTo.defaultReplyToDisplayName ?? '').trim() || null
      })
      .subscribe({
        next: (saved) => {
          // Adopt what was stored rather than what was submitted: the server normalises (a Custom mode
          // with no address becomes Inherit, other modes clear the pair), so assuming the form state
          // survived verbatim is how the panel starts disagreeing with the database.
          this.replyToClean = saved ? { ...saved } : ReplyToDefaults.notSet();
          this.replyTo = { ...this.replyToClean };
          this.replyToDirty = false;
          this.replyToSaving = false;
        },
        error: () => (this.replyToSaving = false)
      });
  }

  private get normalisedReplyToAddress(): string {
    return (this.replyTo.defaultReplyToEmailAddress ?? '').trim();
  }

  /** Loads the "Send test" feature-gate flag for the current environment/region (SB-14894). */
  private loadTestSendEnabled(): void {
    this.api.isTestSendEnabled(this.organisationId).subscribe({
      next: (res) => (this.testSendEnabled = res?.enabled === true),
      error: () => (this.testSendEnabled = false)
    });
  }

  /**
   * The editor says whether the canvas has unsaved edits: Save, Test Send and the leave prompts follow it
   * (SB-14894). It turns false again once the canvas is back to its saved content, e.g. a block added and
   * deleted again, or an edit undone (SB-17105). Header edits count separately, through metaChanged.
   */
  onCanvasDirtyChange(dirty: boolean): void {
    this.canvasDirty = dirty;
  }

  /**
   * The header fields that travel in a save payload. Body and design are covered by `dirty`; these are
   * not bound to any change handler, so they need comparing instead.
   */
  private metaFingerprint(): string | null {
    const t = this.template;
    if (!t) {
      return null;
    }

    return JSON.stringify([
      t.name ?? '',
      t.emailSubject ?? '',
      t.replyToMode ?? ReplyToMode.Inherit,
      t.replyToEmailAddress ?? '',
      t.replyToDisplayName ?? '',
      t.sendToStudents === true,
      t.sendToParents === true,
      t.sendToStaff === true,
      t.isOrganisationFooterIncluded === true,
      t.isEnabled === true
    ]);
  }

  /** Records the current header fields as the saved state. */
  private captureMetaBaseline(): void {
    this.metaBaseline = this.metaFingerprint();
    this.savedHeader = this.template ? this.headerOf(this.template) : null;
  }

  /**
   * Clears dirty state for a selection that is about to load — no baseline until the content lands. The
   * canvas's edits are dropped in the editor, which says so through canvasDirtyChange. The last image-upload
   * failure goes too: it was about the template being left.
   */
  private resetTemplateDirtyState(): void {
    this.editor?.discardEdits?.();
    this.metaBaseline = null;
    this.savedHeader = null;
    this.imageError = null;
    this.imageErrorParams = {};
    this.cancelRenameVersion();
  }

  /** The template-level settings of a template, copied field by field (never its id). */
  private headerOf(t: CommunicationTemplate): TemplateHeader {
    return {
      name: t.name,
      replyToMode: t.replyToMode,
      replyToEmailAddress: t.replyToEmailAddress,
      replyToDisplayName: t.replyToDisplayName,
      sendToStudents: t.sendToStudents,
      sendToParents: t.sendToParents,
      sendToStaff: t.sendToStaff,
      isOrganisationFooterIncluded: t.isOrganisationFooterIncluded,
      isEnabled: t.isEnabled
    };
  }

  /**
   * Puts the template-level settings back as last loaded or saved, for a confirmed discard that keeps the
   * template open. Loading another version replaces only the version's content, so edited settings used to
   * stay on screen and be taken in as the new baseline: shown as saved, unseen by the leave guards, and
   * written by the next save of the active version (SB-17074).
   */
  private revertHeaderEdits(): void {
    if (this.template && this.savedHeader) {
      this.template = { ...this.template, ...this.savedHeader };
    }
  }

  /**
   * After a save through the version endpoints ("Save as new version", or saving a non-active version).
   * They store the version's content and subject, and the template's own settings only when they create
   * the template; an existing template keeps its stored settings. Edits to those settings therefore stay
   * unsaved here, instead of being taken in as saved and dropped by the next switch without a word
   * (SB-17074).
   *
   * @param templateExisted Whether the template (header) was already stored before this save.
   */
  private markVersionSaved(templateExisted: boolean): void {
    if (!templateExisted || !this.template || !this.savedHeader) {
      this.captureMetaBaseline();
      return;
    }

    const edited = this.headerOf(this.template);
    this.template = { ...this.template, ...this.savedHeader };
    this.captureMetaBaseline();
    this.template = { ...this.template, ...edited };
  }

  /** True when a header field differs from the last loaded/saved state. */
  private get metaChanged(): boolean {
    return this.metaBaseline !== null && this.metaFingerprint() !== this.metaBaseline;
  }

  /** Unsaved edits to the selected template — Unlayer content or any header field. */
  get hasUnsavedTemplateChanges(): boolean {
    return this.dirty || this.metaChanged;
  }

  /** Unlayer Project ID from env config; `0`/undefined ⇒ free mode. */
  get unlayerProjectId(): number | undefined {
    return environment.unlayerProjectId || undefined;
  }

  /** Self-hosted Unlayer loader URL from env; empty ⇒ undefined ⇒ default CDN. */
  get unlayerScriptUrl(): string | undefined {
    return environment.unlayerScriptUrl || undefined;
  }

  /**
   * Routes the editor's image uploads to the organisation's SchoolsBuddy storage. Rejects a
   * non-image before it is uploaded (SB-14897); the server enforces the same allow-list, so this is
   * only an early client-side guard that keeps junk from leaving the browser. An image over
   * {@link MAX_IMAGE_UPLOAD_SIZE_MB} MB, which no school accepts, is rejected there too (SB-17177): the API
   * host turns a big enough one away before the server can say why. On rejection the template editor's
   * upload callback reports failure to Unlayer (no URL is inserted) and hands the label tag to
   * {@link onImageUploadFailed}. Every rejection is an {@link ImageUploadRefusal}, whose values also go to
   * {@link imageErrorParams} for the alert above the editor.
   */
  readonly imageUploader = (file: File): Promise<string> => {
    // A new attempt: the last one's failure no longer applies, whichever way this one goes.
    this.imageError = null;
    this.imageErrorParams = {};

    if (!ALLOWED_IMAGE_UPLOAD_TYPES.includes(file.type)) {
      return Promise.reject(new ImageUploadRefusal('SB_Image_Upload_Unsupported_Type'));
    }

    if (exceedsMaxImageUploadSize(file)) {
      this.imageErrorParams = { maximumImageSizeMB: MAX_IMAGE_UPLOAD_SIZE_MB };
      return Promise.reject(new ImageUploadRefusal('SB_Image_Upload_File_Size_Exceeded', this.imageErrorParams));
    }

    return firstValueFrom(this.api.uploadImage(this.organisationId, file))
      .then((res) => res?.url ?? '')
      .catch((response: HttpErrorResponse) => {
        // The server answers with a label tag and the values its text interpolates: the size limit, or the
        // setting that is not a number and the school's id (SB-17179).
        this.imageErrorParams = imageUploadErrorParams(response?.error);
        throw new ImageUploadRefusal(response?.error?.error || 'SB_Library_Image_Upload_Failed', this.imageErrorParams);
      });
  };

  /**
   * Why the last image upload failed, as a label tag. Unlayer only says that it failed, so the editor
   * hands the reason over through `imageUploadFailed`.
   */
  imageError: string | null = null;

  /** Interpolation values for {@link imageError}, e.g. the size limit, or the setting that is not a number. */
  imageErrorParams: ImageUploadErrorParams = {};

  /** The editor could not store an image; say why, since Unlayer cannot. */
  onImageUploadFailed(reason: string): void {
    this.imageError = reason || 'SB_Library_Image_Upload_Failed';
  }

  /**
   * Left-pane rows: every template type, with the organisation's row attached when configured.
   *
   * Recomputed on load and on search change rather than exposed as a getter — the same reason
   * {@link prebuiltByCategory} is a field. A getter rebuilt the Map and a fresh `{ type, template }`
   * object for all ~55 rows on every change-detection pass, and with an Unlayer iframe on the page
   * those passes are near-continuous. Because the objects were new each time, `*ngFor` matched nothing
   * and destroyed and recreated the whole list: the pane flickered, and clicks were lost outright when
   * the `<li>` under the pointer was replaced between mousedown and click.
   */
  catalogue: { type: CommunicationTemplateType; template: CommunicationTemplateSummary | null }[] = [];

  /**
   * The left-pane views: the platform's system template types, the organisation's own custom
   * templates, its email footer, and its Reply-To default. The first two used to be stacked in one
   * column separated by a rule, where the ~55-row catalogue won the height auction and squeezed the
   * custom list into a sliver at the bottom.
   *
   * Rendered as a vertical tablist rather than the shared `<sb-tabs>`: see the comment in the template
   * for why (an 18rem pane cannot hold two 161px tabs, and an sb-tabs tab is not keyboard-reachable).
   */
  readonly listTabs: { id: TemplateListTab; label: string; hint?: string }[] = [
    { id: 'system', label: 'SB_System_Templates', hint: 'SB_System_Templates_Hint' },
    { id: 'custom', label: 'SB_Custom_Templates', hint: 'SB_Custom_Templates_Hint' },
    { id: 'footer', label: 'SB_Email_Footer' },
    { id: 'replyTo', label: 'SB_Reply_To' }
  ];

  activeListTab: TemplateListTab = 'system';

  selectListTab(id: TemplateListTab): void {
    // The two lists share one editor. Opening a list shows its own template - the last one open there,
    // or for Custom templates a blank new one - instead of leaving the other list's on screen. Unsaved
    // edits are asked about as for any other switch, and the tab stays put when the author keeps them.
    const leavesType = id === 'custom' && this.selectionKind !== 'user';
    const leavesUser = id === 'system' && this.selectionKind === 'user';
    if (leavesType || leavesUser) {
      if (!this.confirmLeavingUnsavedTemplate()) {
        return;
      }

      if (leavesType) {
        this.openLastUserTemplateOrNew();
      } else {
        this.openLastType();
      }
    }

    this.activeListTab = id;

    // Mount the footer's TinyMCE only once the tab is actually opened. The pane itself stays in the
    // DOM from the start (its id is what the tablist's aria-controls names), but initialising an
    // editor inside a display:none container gives it zero metrics — and, worse, tinymce-angular
    // writes its normalised content straight back into the model on init, which would mark the
    // footer dirty before the user has touched anything and trip both hosts' unsaved-work guards on
    // a screen nobody edited.
    if (id === 'footer') {
      this.footerTabVisited = true;
    }
  }

  /**
   * The count badge beside a tab label. `null` for the footer and Reply-To tabs, which are single
   * objects rather than lists — the badge's old inline ternary fell through to the type count and would
   * have shown ~55 next to "Email footer". Also `null` while the catalogue loads, since the counts are
   * not known yet and the tablist now renders during the load.
   */
  countFor(tab: { id: TemplateListTab }): number | null {
    if (tab.id === 'footer' || tab.id === 'replyTo' || this.loadingCatalogue) {
      return null;
    }

    return tab.id === 'custom' ? this.userTemplates.length : this.types.length;
  }

  /**
   * Vertical-tablist keyboard model (WAI-ARIA APG): Up/Down move between the views and wrap,
   * Home/End jump to the ends. Selection follows focus, which is the recommended behaviour when
   * switching is cheap — here it only toggles which pane is rendered.
   */
  onSwitchKeydown(event: KeyboardEvent, index: number): void {
    const last = this.listTabs.length - 1;
    let next: number;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    const target = this.listTabs[next];
    this.selectListTab(target.id);
    // The row that is now active is the only one with tabindex="0", so focus has to follow it - which is
    // not the target when the author kept unsaved edits and the switch was refused.
    document.getElementById(`ctmListTab-${this.activeListTab}`)?.focus();
  }

  /** Stable identity for the type rows, so searching reuses the existing nodes. */
  trackCatalogueRow(
    _index: number,
    row: { type: CommunicationTemplateType; template: CommunicationTemplateSummary | null }
  ): number {
    return row.type.id;
  }

  onTypeSearchChange(): void {
    this.rebuildCatalogue();
  }

  private rebuildCatalogue(): void {
    const term = this.typeSearch.trim().toLowerCase();
    const byType = new Map(this.templates.map((t) => [t.communicationTemplateItemId, t]));
    this.catalogue = this.types
      .filter((type) => !term || type.name.toLowerCase().includes(term))
      .map((type) => ({ type, template: byType.get(type.id) ?? null }));
  }

  /**
   * Opens a catalogue type, asking first when the editor holds unsaved edits.
   *
   * Picking another row used to drop them without a word: the canvas is reloaded and `dirty` reset in the
   * same breath. Internal re-selection goes through {@link openType} instead, so a save or a delete that
   * has already happened does not ask about work it just dealt with.
   */
  selectType(typeId: number): void {
    if (!this.confirmLeavingUnsavedTemplate()) {
      return;
    }

    this.openType(typeId);
  }

  /** Selects a user-created template, asking first when the editor holds unsaved edits (SB-14890). */
  selectUserTemplate(id: number): void {
    if (!this.confirmLeavingUnsavedTemplate()) {
      return;
    }

    this.openUserTemplate(id);
  }

  /**
   * Whether it is all right to abandon the open template: true when nothing is unsaved, otherwise
   * whatever the author answers.
   *
   * "Unsaved" covers the header fields as well as the canvas. Checking `dirty` alone asked only about
   * canvas edits, so a changed name, subject, Reply-To, recipient flag or footer checkbox was dropped
   * without a word (SB-17074).
   */
  private confirmLeavingUnsavedTemplate(): boolean {
    if (!this.hasUnsavedTemplateChanges) {
      return true;
    }

    return window.confirm(this.translate.instant('SB_Discard_Template_Changes_Confirm'));
  }

  private openType(typeId: number): void {
    this.loadToken++;
    this.hasSelection = true;
    this.selectionKind = 'type';
    this.resetTemplateDirtyState();
    this.selectedTypeId = typeId;
    this.lastTypeId = typeId;
    this.selectedUserTemplateId = null;
    this.confirmingDeleteVersionId = null;
    this.confirmingDeleteTemplate = false;
    this.userTags = [];
    this.clearDefaultView();
    this.loadTemplate(typeId);
    this.loadVersionsForSelection();
  }

  private openUserTemplate(id: number): void {
    this.loadToken++;
    this.hasSelection = true;
    this.selectionKind = 'user';
    this.resetTemplateDirtyState();
    this.selectedUserTemplateId = id;
    this.lastUserTemplateId = id;
    this.selectedTypeId = null;
    this.confirmingDeleteVersionId = null;
    this.confirmingDeleteTemplate = false;
    this.userTags = this.userTemplates.find((t) => t.id === id)?.tags?.slice() ?? [];
    this.clearDefaultView();
    this.loadUserTemplate(id);
    this.loadVersionsForSelection();
  }

  /**
   * "New template": a blank custom template, straight in the editor, asking first about unsaved edits.
   * The modal with the pre-built gallery only while {@link prebuiltTemplatesEnabled} is on.
   */
  newUserTemplate(): void {
    if (!this.confirmLeavingUnsavedTemplate()) {
      return;
    }

    if (this.prebuiltTemplatesEnabled) {
      this.openCreate();
      return;
    }

    this.openNewUserTemplate();
  }

  /**
   * Puts a blank custom template in the editor. It has no id, versions or tags on the server yet: saving
   * creates it, version 1 and all, and then opens what was created (SB-15966).
   */
  private openNewUserTemplate(): void {
    this.loadToken++;
    this.hasSelection = true;
    this.selectionKind = 'user';
    this.resetTemplateDirtyState();
    this.selectedUserTemplateId = null;
    this.selectedTypeId = null;
    this.confirmingDeleteVersionId = null;
    this.confirmingDeleteTemplate = false;
    this.userTags = [];
    this.clearDefaultView();
    this.versions = [];
    this.selectedVersionId = null;
    this.loadingVersions = false;
    this.loadingTemplate = false;
    this.template = this.blankUserTemplate();
    this.showDesign(null);
    this.captureMetaBaseline();
  }

  /**
   * Hands the editor a design, or an empty canvas for none. The editor reloads when its `design` input
   * changes, and null after null is no change: a blank template opened after another blank one would
   * keep what was typed into that one, so the canvas is emptied explicitly.
   */
  private showDesign(design: object | null): void {
    if (!design && this.design === null) {
      this.editor?.clear();
    }
    this.design = design;
  }

  /**
   * After a reload that cleared the selection: Custom templates shows a blank new template, as opening the
   * tab does - unless the author has opened something while the list was loading.
   */
  private openNewUserTemplateIfIdle(): void {
    if (this.activeListTab === 'custom' && this.selectionKind === null) {
      this.openNewUserTemplate();
    }
  }

  /** The custom template last open, if it still exists, otherwise a blank new one. */
  private openLastUserTemplateOrNew(): void {
    const id = this.lastUserTemplateId;
    if (id != null && this.userTemplates.some((t) => t.id === id)) {
      this.openUserTemplate(id);
    } else {
      this.openNewUserTemplate();
    }
  }

  /** The type last open, or nothing - the "select a template" hint - when none has been yet. */
  private openLastType(): void {
    if (this.lastTypeId != null) {
      this.openType(this.lastTypeId);
    } else {
      this.resetSelection();
    }
  }

  /** True when the editor is on a user-created template rather than a catalogue type. */
  get isUserTemplate(): boolean {
    return this.selectionKind === 'user';
  }

  /**
   * The merge tags the editor offers for the open template: a custom template gets no Message text, since it is
   * itself the message (SB-17215). One of two stored maps, so the editor is handed a new set only when the kind of
   * template changes.
   */
  get editorMergeTags(): UnlayerMergeTags {
    return this.isUserTemplate ? this.customTemplateMergeTags : this.mergeTags;
  }

  /**
   * The subject limit for the open template: the Subject box's `maxlength` and the counter under it. Only a custom
   * template has one, since only its subject is copied into the 250-character column on a send (SB-17213).
   */
  get subjectMaxLength(): number | null {
    return this.isUserTemplate ? USER_TEMPLATE_SUBJECT_MAX_LENGTH : null;
  }

  /**
   * True when a custom template's subject is over the limit: one stored before it existed, which `maxlength` does
   * not shorten, so the counter turns red and Save says why it refuses until the author shortens it (SB-17213).
   */
  get subjectTooLong(): boolean {
    const max = this.subjectMaxLength;
    return max != null && (this.template?.emailSubject?.length ?? 0) > max;
  }

  /** True when the editor is on a custom template that saving will create (see {@link openNewUserTemplate}). */
  get isNewUserTemplate(): boolean {
    return this.isUserTemplate && this.selectedUserTemplateId == null && this.template != null;
  }

  /**
   * Whether to say that a required field is empty. Not on a blank new template nobody has changed yet:
   * opening Custom templates should not greet the author with errors. Once anything changes, the hints
   * say why Save is still off.
   */
  get showRequiredHints(): boolean {
    return !this.isNewUserTemplate || this.hasUnsavedTemplateChanges;
  }

  /**
   * The heading over the editor: the type's catalogue name, or the custom template's saved name. Not
   * `template.templateName`, which a seeded or blank type carries as its numeric id.
   */
  get selectionTitle(): string | null {
    if (this.selectionKind === 'type') {
      return this.types.find((t) => t.id === this.selectedTypeId)?.name ?? null;
    }
    if (this.selectionKind === 'user') {
      return this.userTemplates.find((t) => t.id === this.selectedUserTemplateId)?.name ?? null;
    }
    return null;
  }

  /** The version currently loaded in the editor (null ⇒ the active/header default or a brand-new type). */
  get selectedVersion(): CommunicationTemplateVersionSummary | null {
    return this.versions.find((v) => v.id === this.selectedVersionId) ?? null;
  }

  /**
   * Who last changed the selected version and when (SB-15966): its last edit or later activation (the server
   * stamps both as the modification), or its creation when neither has happened since. `name` is left out when unknown — the first version of every template that predates
   * versioning was backfilled without an author — and the label then omits it. `date` is organisation
   * time, for the template's date pipe; null while the read-only Default is on screen.
   */
  get selectedVersionChange(): { label: string; name?: string; date: string } | null {
    const v = this.selectedVersion;
    if (!v || this.viewingDefault) {
      return null;
    }

    const edited = v.modifiedDateUtc != null;
    const name = (edited ? v.modifiedByPersonName : v.createdByPersonName) || undefined;

    // The local form comes from DateTimeInterceptor. Should a response ever bypass it, read the UTC value
    // as the instant it is rather than as a zone-less local time.
    const date = edited ? (v.modifiedDate ?? asUtc(v.modifiedDateUtc)) : (v.createdDate ?? asUtc(v.createdDateUtc));

    let label: string;
    if (edited) {
      label = name ? 'SB_Version_Updated_By' : 'SB_Version_Updated';
    } else {
      label = name ? 'SB_Version_Created_By' : 'SB_Version_Created';
    }

    return { label, name, date };
  }

  /** True when the editor is on the active version (or a brand-new template with no versions). */
  get isEditingActiveVersion(): boolean {
    if (this.viewingDefault) {
      return false;
    }

    const v = this.selectedVersion;
    return !v || v.isActive;
  }

  /** Sentinel exposed to the template so the `<select>` can offer the Default option. */
  get defaultVersionId(): number {
    return DEFAULT_VERSION_ID;
  }

  /**
   * The version dropdown is locked while anything that will rewrite the version state is in flight.
   * Changing selection mid-flight is a real hazard rather than a cosmetic one: an export that resolves
   * after a switch to the Default is silently dropped, and an activate/delete that completes while the
   * read-only pane is up would overwrite the selection behind it.
   */
  get versionSelectDisabled(): boolean {
    return (
      this.renamingVersionId != null ||
      this.loadingVersions ||
      this.saving ||
      this.previewing ||
      this.mutatingVersion ||
      this.copyingFromDefault ||
      (!this.versions.length && !this.canSelectDefault)
    );
  }

  /** The Default is offered for catalogue types only, and only when one actually exists. */
  get canSelectDefault(): boolean {
    return this.selectionKind === 'type' && this.defaultTemplate != null;
  }

  /**
   * True when the default carries nothing to show on any channel (belt-and-braces; the server filters
   * these out). Push counts — see {@link defaultIsPushOnly}.
   */
  get defaultIsEmpty(): boolean {
    return !this.defaultTemplate?.emailBody?.trim() && !this.defaultTemplate?.pushNotification?.trim();
  }

  /**
   * True when this type is push-only: no email body, but push text present. The transport-notification
   * types are like this by design — `TransportNotificationProcessor` bails out unless `SendPush` and
   * renders `PushNotification`, never touching subject/body. Without this the pane claimed "no content"
   * for templates that do have content, just on a channel the email preview cannot show.
   */
  get defaultIsPushOnly(): boolean {
    return !this.defaultTemplate?.emailBody?.trim() && !!this.defaultTemplate?.pushNotification?.trim();
  }

  /**
   * The copy CTA is deliberately hidden for a type the organisation has no row for. Two reasons: the
   * canvas is *already* a seeded copy of the default there, so the button would be a no-op to the user;
   * and copying would call `CreateVersionAsync` with no header, which creates one with `IsEnabled = true`
   * hard-coded and then force-activates it despite `activate: false` — silently switching on a template
   * type that previously dispatched nothing.
   */
  get canCopyFromDefault(): boolean {
    return this.viewingDefault && !this.defaultIsEmpty && !this.copyingFromDefault && (this.template?.id ?? 0) > 0;
  }

  /** True when the canvas is showing a not-yet-saved copy of the default (unconfigured type). */
  get isSeededFromDefault(): boolean {
    return this.selectionKind === 'type' && this.defaultTemplate != null && (this.template?.id ?? 0) === 0;
  }

  /**
   * True when the canvas has no stored row behind it yet: a custom template never saved, or a type the
   * organisation has not customised (seeded from the platform default, or blank when there is none). Save
   * is live even with nothing edited, and a test send has nothing stored to send until that first save
   * (SB-17217).
   */
  get needsFirstSave(): boolean {
    const typeWithNoRow = this.selectionKind === 'type' && this.template != null && (this.template.id ?? 0) === 0;
    return this.isNewUserTemplate || typeWithNoRow;
  }

  /** Preview is allowed once the editor is ready and no export is already in flight. */
  get canPreview(): boolean {
    return this.editorReady && !this.saving && !this.previewing && !this.viewingDefault;
  }

  /**
   * Save additionally requires a non-empty subject; when saving the active version (which also persists
   * header settings) a supplied reply-to email must be valid too.
   */
  get canSave(): boolean {
    if (!this.canPreview || !this.template?.emailSubject?.trim()) {
      return false;
    }
    if (this.isUserTemplate && !this.template?.name?.trim()) {
      return false;
    }

    // Nothing to save unless something changed, or nothing is stored yet ({@link needsFirstSave}): saving
    // an uncustomised type unchanged is exactly how the organisation takes its own copy (SB-14943 Q4).
    if (!this.hasUnsavedTemplateChanges && !this.needsFirstSave) {
      return false;
    }

    if (!this.isEditingActiveVersion) {
      return true;
    }

    // A Custom override with no address is a 400 from the server, so block it here rather than let the
    // author discover it from a toast (SB-15933).
    return this.replyToEmailValid && !this.templateReplyToAddressMissing;
  }

  /**
   * "Save as new version" needs the editor ready and a non-empty subject - and a template to add the
   * version to, which a new custom template is not until its first save.
   */
  get canSaveAsNewVersion(): boolean {
    return this.canPreview && !!this.template?.emailSubject?.trim() && !this.isNewUserTemplate;
  }

  /** A reply-to email is optional; when present it must be a well-formed address. */
  get replyToEmailValid(): boolean {
    const email = this.template?.replyToEmailAddress?.trim();
    return !email || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  }

  // ---- Per-template Reply-To override (SB-15933) ----

  /**
   * Toggles the override checkbox. On means "start from a custom address"; off returns the template to
   * the organisation's default and clears the pair, since the server drops it for any non-Custom mode
   * and leaving it visible would show an address that is about to be discarded.
   */
  onOverrideReplyToChange(template: CommunicationTemplate, event: Event): void {
    const on = (event.target as HTMLInputElement).checked;

    template.replyToMode = on ? ReplyToMode.Custom : ReplyToMode.Inherit;

    if (!on) {
      template.replyToEmailAddress = undefined;
      template.replyToDisplayName = undefined;
    }
  }

  /** Switches between Custom and No reply inside the override, clearing the pair on the way to No reply. */
  onTemplateReplyToModeChange(template: CommunicationTemplate, mode: ReplyToMode): void {
    template.replyToMode = mode;

    if (mode !== ReplyToMode.Custom) {
      template.replyToEmailAddress = undefined;
      template.replyToDisplayName = undefined;
    }
  }

  /** True while the template overrides with a custom address but has not supplied one. */
  get templateReplyToAddressMissing(): boolean {
    return (
      this.template?.replyToMode === ReplyToMode.Custom &&
      !this.template?.replyToEmailAddress?.trim()
    );
  }

  /** Guidance for the override's selected mode — the organisation-level wording applies verbatim. */
  get templateReplyToGuidanceLabel(): string {
    return this.template?.replyToMode === ReplyToMode.NoReply
      ? 'SB_Reply_To_No_Reply_Guidance'
      : 'SB_Reply_To_Custom_Guidance';
  }

  /**
   * Which "inherits" line to show while the override is off — a separate label for the case where the
   * organisation has named no address of its own, which spells out the platform no-reply the template
   * will actually send with rather than leaving the author to guess where a reply lands.
   */
  get inheritedReplyToLabel(): string {
    return this.inheritedReplyToText ? 'SB_Reply_To_Inherited' : 'SB_Reply_To_Inherited_None';
  }

  /**
   * What the template inherits, in the organisation panel's own terms. Read from the same loaded
   * settings object the Reply-To tab edits, so the hint cannot disagree with what that tab shows.
   *
   * Empty only where the organisation has chosen nothing: the two states resolve to the same header
   * today, but "No reply" is a choice the school made and worth naming as one.
   */
  get inheritedReplyToText(): string {
    if (this.replyToLoading || this.replyTo.replyToMode === ReplyToMode.Inherit) {
      return '';
    }

    if (this.replyTo.replyToMode === ReplyToMode.NoReply) {
      return this.translate.instant('SB_Reply_To_Mode_No_Reply');
    }

    const address = (this.replyTo.defaultReplyToEmailAddress ?? '').trim();
    const name = (this.replyTo.defaultReplyToDisplayName ?? '').trim();

    if (!address) {
      return '';
    }

    return name ? `${name} <${address}>` : address;
  }

  /** The mode's label, for the read-only Default panel. */
  replyToModeLabel(mode: ReplyToMode | undefined): string {
    switch (mode) {
      case ReplyToMode.Custom:
        return 'SB_Reply_To_Mode_Custom';
      case ReplyToMode.NoReply:
        return 'SB_Reply_To_Mode_No_Reply';
      default:
        return 'SB_Reply_To_Mode_Inherit';
    }
  }

  onEditorReady(): void {
    this.editorReady = true;
  }

  save(): void {
    if (!this.canSave || !this.editor || !this.template) {
      return;
    }

    this.pendingAction = 'save';
    this.saving = true;
    this.editor.exportHtml();
  }

  preview(): void {
    if (!this.canPreview || !this.editor || !this.template) {
      return;
    }

    this.pendingAction = 'preview';
    this.previewing = true;
    this.editor.exportHtml();
  }

  /**
   * A test-send recipient address is required and must be a bare, well-formed email of at most 320 characters.
   * It accepts no more than the server does (SB-17218: the length check, then `MailAddress` parsing compared back
   * to the input), so a value the button lets through is not then refused: dot-separated atoms on both sides of
   * the `@`, which rules out two dots in a row, display-name forms (`Sports<a@b.com>`), comments, commas and spaces.
   */
  get testEmailValid(): boolean {
    const email = this.testEmail?.trim();
    return !!email && email.length <= TEST_EMAIL_MAX_LENGTH && TEST_EMAIL_PATTERN.test(email);
  }

  /**
   * A test sends the STORED active template, so anything on screen not stored yet blocks it: an edit to the
   * canvas or to any header field (subject, footer flag, reply-to…), or nothing stored at all yet
   * ({@link needsFirstSave}). The Send test gate and the hint under it read this same getter on purpose —
   * keep them shared (SB-17217).
   */
  get testSendNeedsSave(): boolean {
    return this.hasUnsavedTemplateChanges || this.needsFirstSave;
  }

  /**
   * Test Send is allowed when the feature is enabled, the editor is ready, no send or save is in flight,
   * nothing needs saving first ({@link testSendNeedsSave}), the default is not being viewed, a valid
   * recipient is entered, and a stored template is selected — a test sends the SAVED active version, so
   * the author must save first (SB-14894, decision 9).
   */
  get canSendTest(): boolean {
    return (
      this.testSendEnabled &&
      this.editorReady &&
      !this.sendingTest &&
      !this.saving &&
      !this.testSendNeedsSave &&
      !this.viewingDefault &&
      this.testEmailValid &&
      (this.selectedTypeId != null || this.selectedUserTemplateId != null)
    );
  }

  /**
   * Sends a test email of the SAVED template to {@link testEmail}. Unlike preview, this does not export
   * the editor — it references the saved template by its selector; the backend renders sample data and
   * routes the message straight to the v2 lane (SB-14894). A custom template goes inside the school's standard
   * message, as Preview shows it; a school without one gets a refusal with its reason (SB-17220).
   */
  sendTest(): void {
    if (!this.canSendTest) {
      return;
    }

    const request: TestSendRequest = {
      toEmailAddress: this.testEmail.trim(),
      communicationTemplateItemId: this.isUserTemplate ? undefined : this.selectedTypeId ?? undefined,
      userTemplateId: this.isUserTemplate ? this.selectedUserTemplateId ?? undefined : undefined
    };

    this.sendingTest = true;
    this.api.testSend(this.organisationId, request).subscribe({
      next: () => (this.sendingTest = false),
      error: (err: HttpErrorResponse) => {
        this.sendingTest = false;
        // A refused recipient or subject comes back as 400 { error: <label key> } (SB-17218, SB-17213), and any
        // other failure as 500 { error: 'SB_Test_Send_Failed' } (SB-17213).
        this.showRefusal(err);
      }
    });
  }

  /**
   * Shows the translation of the label key a request was refused with, as `{ error: <key> }` (SB-17218, SB-17213).
   * The shared HTTP error toast shows nothing for that shape, so without this the author would see no reason at
   * all; any other failure is a plain string the shared toast has already shown, and is left alone.
   */
  private showRefusal(err: HttpErrorResponse): void {
    const key = err?.error?.error;
    if (typeof key === 'string' && key) {
      this.toast.saveError(this.translate.instant(key));
    }
  }

  /** Exports the current editor content and saves it as a brand-new (inactive) version. */
  saveAsNewVersion(): void {
    if (!this.canSaveAsNewVersion || !this.editor || !this.template) {
      return;
    }

    this.pendingAction = 'saveAsNew';
    this.saving = true;
    this.editor.exportHtml();
  }

  onHtmlExport(result: TemplateEditorExport): void {
    // Hard stop while the read-only Default is on screen. Every caller is already gated, but an export
    // is asynchronous: one that was in flight when the author switched to the default would otherwise
    // land here and persist content over the organisation's live template.
    if (this.viewingDefault) {
      this.saving = false;
      this.previewing = false;
      return;
    }

    if (this.pendingAction === 'preview') {
      this.showPreview(result.html);
      return;
    }

    this.saveExport(result);
  }

  /** Renders exported HTML through the server, with sample data, and opens the preview. */
  private showPreview(html: string): void {
    // Pass the template's own footer setting, so the preview shows the bottom of the email exactly
    // as this template would send it (SB-14896) rather than the body alone. A custom template is shown inside the
    // school's standard message with its subject as the heading, as its test email shows it (SB-17220).
    const includeFooter = this.template?.isOrganisationFooterIncluded === true;
    const options = { isUserTemplate: this.isUserTemplate, subject: this.template?.emailSubject ?? null };
    this.api.preview(this.organisationId, html, includeFooter, options).subscribe({
      next: res => {
        this.previewing = false;
        this.previewSafeHtml = this.sanitizer.bypassSecurityTrustHtml(res?.html ?? '');
        this.previewUnrecognisedTags = res?.unrecognisedTags ?? [];
        this.previewDevice = 'desktop';
        this.modal.open(this.previewModal, { size: 'xl', scrollable: true });
      },
      error: () => {
        this.previewing = false;
        this.previewUnrecognisedTags = [];
      }
    });
  }

  /**
   * Stores an export where the selection and the action say: a new custom template, a new version, the open
   * non-active version, or the template itself.
   */
  private saveExport(result: TemplateEditorExport): void {
    const hasTarget = this.selectedTypeId != null || this.selectedUserTemplateId != null || this.isNewUserTemplate;
    if (!this.template || !hasTarget) {
      return;
    }

    // A double space or a tab pasted in around a merge field is never wanted in an inbox, so every save
    // stores the subject with whitespace runs collapsed and trimmed.
    this.template.emailSubject = normaliseSubject(this.template.emailSubject);

    // A custom template subject over the limit (one stored before it existed) is refused here, for every save
    // path alike, before any request. The server refuses it too (SB-17213), but this shows the subject's own
    // error at once, and activating a stored over-long version would copy it into the template unchecked.
    if (this.subjectTooLong) {
      this.saving = false;
      this.toast.saveError(this.translate.instant('SB_Template_Subject_Too_Long'));
      return;
    }

    // A custom template may not hold the Message text tag, in the subject or the body (SB-17215): one saved before
    // the rule, or typed in by hand, is refused here with the server's reason, before any request.
    if (this.isUserTemplate && containsMessageTextTag(this.template.emailSubject, result.html)) {
      this.saving = false;
      this.toast.saveError(this.translate.instant('SB_Template_Message_Text_Not_Allowed'));
      return;
    }

    const canvas = this.startCanvasSave(result);
    if (this.isNewUserTemplate) {
      this.storeNewUserTemplate(result, canvas);
    } else if (this.pendingAction === 'saveAsNew') {
      this.storeNewVersion(result, canvas);
    } else if (!this.isEditingActiveVersion && this.selectedVersionId != null) {
      this.storeOpenVersion(this.selectedVersionId, result, canvas);
    } else {
      this.storeTemplate(result, canvas);
    }
  }

  /**
   * Tells the editor that `result` is being stored, and returns how to tell it the save's outcome (SB-17105):
   * until then the canvas does not read back to saved, and once it lands what it stored is the saved content.
   * The outcome is told for the selection that was saved only.
   */
  private startCanvasSave(result: TemplateEditorExport): CanvasSave {
    this.editor?.saveStarted?.(result);
    const token = this.loadToken;
    return {
      landed: () => {
        if (token === this.loadToken) {
          this.editor?.markSaved?.(result);
        }
      },
      failed: () => {
        if (token === this.loadToken) {
          this.editor?.saveFailed?.(result);
        }
      }
    };
  }

  /**
   * A new custom template does not exist until this first save: create it, content, tags and version 1
   * together, then open what was created. Save as new version is not offered until then.
   */
  private storeNewUserTemplate(result: TemplateEditorExport, canvas: CanvasSave): void {
    this.api.createUserTemplate(this.organisationId, this.userSaveRequest(result)).subscribe({
      next: created => {
        this.saving = false;
        this.captureMetaBaseline();
        canvas.landed();
        this.loadCatalogue(created?.id ? () => this.openUserTemplate(created.id) : undefined);
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        canvas.failed();
        this.showRefusal(err); // e.g. a subject over the limit (SB-17213)
      }
    });
  }

  /** "Save as new version": the export becomes a new, inactive version of the open template. */
  private storeNewVersion(result: TemplateEditorExport, canvas: CanvasSave): void {
    // A type the organisation has never configured gets its template created by this save, flags included.
    const templateExisted = this.isUserTemplate || (this.template!.id ?? 0) > 0;
    const create$ = this.isUserTemplate
      ? this.api.createUserTemplateVersion(
          this.organisationId,
          this.selectedUserTemplateId!,
          this.versionRequest(result),
          false
        )
      : this.api.createVersion(this.organisationId, this.selectedTypeId!, this.versionRequest(result), false);
    create$.subscribe({
      next: created => {
        this.saving = false;
        this.markVersionSaved(templateExisted);
        canvas.landed();
        this.selectedVersionId = created?.id ?? this.selectedVersionId;
        this.loadVersionsForSelection(created?.id);
        this.loadCatalogue();
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        canvas.failed();
        this.showRefusal(err); // e.g. a custom template subject over the limit (SB-17213)
      }
    });
  }

  /**
   * A non-active version is open: the export updates just that version's content in place (shared version
   * endpoint for both kinds of template).
   *
   * @param savedVersionId Captured before the reload: `loadVersionsForSelection` clears `selectedVersionId` on
   * the way in.
   */
  private storeOpenVersion(savedVersionId: number, result: TemplateEditorExport, canvas: CanvasSave): void {
    this.api.updateVersion(this.organisationId, savedVersionId, this.versionRequest(result)).subscribe({
      next: () => {
        this.saving = false;
        this.markVersionSaved(true);
        canvas.landed();

        // Stay on the version that was just written. Without the id the reload falls back to the
        // ACTIVE version, so saving a non-active version (the usual shape right after "copy from
        // default", which lands on a new inactive version) bounced the author onto the first/active
        // version while the canvas still held the edited one. Worse than cosmetic: with the dropdown
        // on the active version `isEditingActiveVersion` becomes true, and the next save would write
        // this content into the live version instead.
        this.loadVersionsForSelection(savedVersionId);
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        canvas.failed();
        this.showRefusal(err);
      }
    });
  }

  /** Plain save of the active version: its content and the template's header settings. */
  private storeTemplate(result: TemplateEditorExport, canvas: CanvasSave): void {
    const save$ = this.isUserTemplate
      ? this.api.saveUserTemplate(this.organisationId, this.selectedUserTemplateId!, this.userSaveRequest(result))
      : this.api.save(this.organisationId, this.typeSaveRequest(result));

    save$.subscribe({
      next: (saved) => {
        this.saving = false;
        this.captureMetaBaseline();
        canvas.landed();

        // Adopt the id the server just allocated. A type the organisation had never configured was
        // seeded with `id: 0`; without this it keeps reporting itself as an unsaved seed of the
        // platform default — the "pre-filled from the default" hint stays up and the copy action
        // stays hidden — even though a real header row now exists.
        if (saved?.id && this.template) {
          this.template = { ...this.template, id: saved.id };
        }

        this.loadCatalogue(); // refresh "configured"/name state for the just-saved template
        this.loadVersionsForSelection();
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        canvas.failed();
        this.showRefusal(err); // e.g. a custom template subject over the limit (SB-17213)
      }
    });
  }

  /** Builds the type-template save payload from the current editor export + header fields. */
  private typeSaveRequest(result: TemplateEditorExport): SaveCommunicationTemplateRequest {
    return {
      communicationTemplateItemId: this.selectedTypeId!,
      emailSubject: this.template!.emailSubject,
      emailBody: result.html,
      pushNotification: this.template!.pushNotification,
      designJson: JSON.stringify(result.design),
      isUnlayerBased: true,
      isEnabled: this.template!.isEnabled,
      sendToStudents: this.template!.sendToStudents,
      sendToParents: this.template!.sendToParents,
      sendToStaff: this.template!.sendToStaff ?? false,
      isOrganisationFooterIncluded: this.template!.isOrganisationFooterIncluded,

      // The mode is authoritative server-side (SB-15933): a payload carrying an address but no mode
      // would be stored as Inherit and the address discarded.
      replyToMode: this.template!.replyToMode ?? ReplyToMode.Inherit,
      replyToEmailAddress: this.template!.replyToEmailAddress,
      replyToDisplayName: this.template!.replyToDisplayName
    };
  }

  /** Builds the user-template save payload (name + content + flags + tags) from the current export. */
  private userSaveRequest(result: TemplateEditorExport): SaveUserTemplateRequest {
    return {
      name: this.template!.name!.trim(),
      emailSubject: this.template!.emailSubject,
      emailBody: result.html,
      pushNotification: this.template!.pushNotification,
      designJson: JSON.stringify(result.design),
      isUnlayerBased: true,
      isEnabled: this.template!.isEnabled,
      sendToStudents: this.template!.sendToStudents,
      sendToParents: this.template!.sendToParents,
      sendToStaff: this.template!.sendToStaff ?? false,
      isOrganisationFooterIncluded: this.template!.isOrganisationFooterIncluded,

      // The mode is authoritative server-side (SB-15933): a payload carrying an address but no mode
      // would be stored as Inherit and the address discarded.
      replyToMode: this.template!.replyToMode ?? ReplyToMode.Inherit,
      replyToEmailAddress: this.template!.replyToEmailAddress,
      replyToDisplayName: this.template!.replyToDisplayName,
      tags: this.userTags
    };
  }

  /** Dropdown change handler: shows the platform Default, or loads the picked version into the editor. */
  onVersionChange(versionId: number | null): void {
    if (versionId === DEFAULT_VERSION_ID) {
      this.viewDefault();
      return;
    }

    // The "Draft (not saved)" placeholder — the only option when a type has no saved versions yet.
    if (versionId == null) {
      this.exitDefault();
      return;
    }

    // Returning to the version we were on before peeking: the canvas still holds it (and any unsaved
    // edits), so just drop the overlay rather than refetching and discarding the author's work.
    if (this.viewingDefault && versionId === this.versionBeforeDefault) {
      this.viewingDefault = false;
      this.confirmingCopyDiscard = false;
      this.selectedVersionId = versionId;
      this.versionBeforeDefault = null;
      return;
    }

    const version = this.versions.find((v) => v.id === versionId);
    if (!version) {
      return;
    }

    // Loading another version replaces the canvas, so unsaved edits go with it. `[(ngModel)]` has already
    // moved the dropdown by the time this fires, so a refusal has to put it back or the control would
    // point at a version the editor is not showing.
    if (!this.confirmLeavingUnsavedTemplate()) {
      const current = this.selectedVersionId;
      this.selectedVersionId = null;
      setTimeout(() => (this.selectedVersionId = current));
      return;
    }

    this.selectVersion(version);
  }

  /**
   * Shows the platform Default read-only, over the top of the live editor. The canvas is left mounted
   * and untouched so the author's (possibly unsaved) work is still there when they come back.
   */
  viewDefault(): void {
    if (!this.canSelectDefault || this.viewingDefault) {
      return;
    }

    this.versionBeforeDefault = this.selectedVersionId;
    this.viewingDefault = true;
    this.selectedVersionId = DEFAULT_VERSION_ID; // reflect the choice in the dropdown only
    this.confirmingDeleteVersionId = null;
    this.cancelRenameVersion();
    this.confirmingCopyDiscard = false;
    this.defaultRenderError = false;

    // Already rendered for this type, nothing to render, or push-only (no email body to preview —
    // the pane shows the push text directly instead).
    if (this.defaultSafeHtml || this.defaultIsEmpty || this.defaultIsPushOnly) {
      return;
    }

    // Guard the response against the selection moving on. `clearDefaultView()` cannot cancel an
    // in-flight render, so without this a late response would repopulate `defaultSafeHtml` after the
    // user switched type — and the cache short-circuit above would then treat that stale HTML as the
    // new type's default, showing one email while the summary and the copy action describe another.
    const token = this.loadToken;

    this.loadingDefault = true;
    this.api.preview(
      this.organisationId,
      this.defaultTemplate!.emailBody!,
      this.defaultTemplate!.isOrganisationFooterIncluded === true).subscribe({
      next: (res) => {
        if (token !== this.loadToken) {
          return;
        }

        this.defaultSafeHtml = this.sanitizer.bypassSecurityTrustHtml(res?.html ?? '');
        this.defaultUnrecognisedTags = res?.unrecognisedTags ?? [];
        this.loadingDefault = false;
      },
      error: () => {
        if (token !== this.loadToken) {
          return;
        }

        this.loadingDefault = false;
        this.defaultUnrecognisedTags = [];
        this.defaultRenderError = true;
      }
    });
  }

  /**
   * Drops the read-only overlay and returns to whatever the editor was on.
   *
   * The remembered version is revalidated against the current list: it can have been deleted (or
   * superseded) while the overlay was up, and restoring a dangling id would make `selectedVersion`
   * null — which `isEditingActiveVersion` reads as "on the active version", unlocking the header
   * fields and routing the next save down the header branch.
   */
  exitDefault(): void {
    if (!this.viewingDefault) {
      return;
    }

    const previous = this.versionBeforeDefault;
    const stillExists = previous != null && this.versions.some((v) => v.id === previous);

    this.viewingDefault = false;
    this.confirmingCopyDiscard = false;
    this.selectedVersionId = stillExists ? previous : this.versions.find((v) => v.isActive)?.id ?? null;
    this.versionBeforeDefault = null;
  }

  /** Copying discards unsaved edits, header fields included, so confirm first when there are any. */
  askCopyFromDefault(): void {
    if (!this.canCopyFromDefault) {
      return;
    }

    if (this.hasUnsavedTemplateChanges) {
      this.confirmingCopyDiscard = true;
      return;
    }

    this.copyFromDefault();
  }

  cancelCopyFromDefault(): void {
    this.confirmingCopyDiscard = false;
  }

  /**
   * Copies the default into a new INACTIVE version of the organisation's own template for this type,
   * then opens it. Inactive by design: the live template keeps dispatching until the author reviews the
   * copy and activates it explicitly (the epic's explicit-version model).
   *
   * The payload carries the default's `isUnlayerBased`/`designJson` verbatim rather than going through
   * `versionRequest()`, which hard-codes `isUnlayerBased: true` — a legacy HTML default copied that way
   * would be mislabelled as an Unlayer design. The server sanitises the HTML on create.
   */
  copyFromDefault(): void {
    if (!this.canCopyFromDefault || this.selectedTypeId == null || !this.defaultTemplate) {
      return;
    }

    const source = this.defaultTemplate;
    const typeId = this.selectedTypeId;
    const request: SaveCommunicationTemplateVersionRequest = {
      name: this.translate.instant('SB_Copy_Of_Default'),
      // Stored as every other save stores it: some library subjects carry tabs and line breaks.
      emailSubject: normaliseSubject(source.emailSubject),
      emailBody: source.emailBody,
      pushNotification: source.pushNotification,
      designJson: source.designJson,
      isUnlayerBased: source.isUnlayerBased,

      // SB-15889: the stock entry's flags travel with its content, so a header created by this copy
      // starts with the curator's settings instead of the server's literals.
      ...this.headerFlags(source)
    };

    this.copyingFromDefault = true;
    this.confirmingCopyDiscard = false;
    this.api.createVersion(this.organisationId, typeId, request, false).subscribe({
      next: (created) => {
        this.copyingFromDefault = false;
        this.viewingDefault = false;
        this.versionBeforeDefault = null;
        // The author agreed to discard unsaved edits; without this, settings edits would be adopted as saved.
        this.revertHeaderEdits();
        this.captureMetaBaseline();
        this.selectedVersionId = created?.id ?? null;
        this.loadVersionsForSelection(created?.id);
        this.loadCatalogue();
        if (created) {
          this.selectVersion({ ...created, isActive: created.isActive ?? false });
        }
      },
      error: () => {
        this.copyingFromDefault = false;
      }
    });
  }

  /** Clears all Default-pane state — called whenever the selection or organisation changes. */
  private clearDefaultView(): void {
    this.viewingDefault = false;
    this.defaultTemplate = null;
    this.defaultSafeHtml = null;
    this.defaultUnrecognisedTags = [];
    this.versionBeforeDefault = null;
    this.loadingDefault = false;
    this.defaultRenderError = false;
    this.copyingFromDefault = false;
    this.confirmingCopyDiscard = false;
    this.defaultPreviewDevice = 'desktop';
  }

  /**
   * Loads a version's content into the editor. The template's own settings go back to their saved values:
   * every caller has already had the author agree to discard unsaved edits (SB-17074).
   */
  selectVersion(version: CommunicationTemplateVersionSummary): void {
    if (!this.template) {
      return;
    }

    this.viewingDefault = false;
    this.versionBeforeDefault = null;
    this.confirmingCopyDiscard = false;
    this.confirmingDeleteVersionId = null;
    this.revertHeaderEdits();
    this.resetTemplateDirtyState();
    this.selectedVersionId = version.id; // reflect the choice in the dropdown immediately
    this.loadingTemplate = true;
    this.api.getVersion(this.organisationId, version.id).subscribe({
      next: (full: CommunicationTemplateVersion) => {
        this.template = {
          ...this.template!,
          emailSubject: full.emailSubject,
          emailBody: full.emailBody,
          pushNotification: full.pushNotification,
          designJson: full.designJson,
          isUnlayerBased: full.isUnlayerBased
        };
        this.design = this.buildDesign(this.template);
        this.captureMetaBaseline();
        this.loadingTemplate = false;
      },
      error: () => {
        this.loadingTemplate = false;
      }
    });
  }

  /** Makes a version the active (live) one, then refreshes the list and catalogue. */
  activateVersion(version: CommunicationTemplateVersionSummary): void {
    if (version.isActive) {
      return;
    }

    const activate$ = this.isUserTemplate
      ? (this.selectedUserTemplateId == null ? null : this.api.activateUserTemplateVersion(this.organisationId, this.selectedUserTemplateId, version.id))
      : (this.selectedTypeId == null ? null : this.api.activateVersion(this.organisationId, this.selectedTypeId, version.id));
    if (!activate$) {
      return;
    }

    this.mutatingVersion = true;
    activate$.subscribe({
      next: () => {
        this.mutatingVersion = false;
        this.selectedVersionId = version.id;
        this.loadVersionsForSelection(version.id);
        this.loadCatalogue();
      },
      error: () => {
        // validation/other errors are surfaced by the shared HTTP error toast
        this.mutatingVersion = false;
      }
    });
  }

  /** A saved version is on screen and nothing that rewrites the version state is in flight. */
  get canRenameVersion(): boolean {
    return (
      !!this.selectedVersion &&
      !this.viewingDefault &&
      !this.loadingTemplate &&
      !this.loadingVersions &&
      !this.saving &&
      !this.previewing &&
      !this.mutatingVersion &&
      !this.copyingFromDefault
    );
  }

  /**
   * Opens the selected version's name for editing (SB-17138). The name belongs to the version, not to the
   * content, so it is saved on its own: unsaved canvas or settings edits stay as they are.
   */
  startRenameVersion(): void {
    const version = this.selectedVersion;
    if (!version || !this.canRenameVersion) {
      return;
    }

    this.confirmingDeleteVersionId = null;
    this.renamingVersionId = version.id;
    this.versionNameDraft = version.name ?? '';
  }

  cancelRenameVersion(): void {
    this.renamingVersionId = null;
    this.versionNameDraft = '';
  }

  /** Saves the typed name for the version being renamed; an unchanged name just closes the field. */
  saveVersionName(): void {
    const versionId = this.renamingVersionId;
    if (versionId == null || this.mutatingVersion) {
      return;
    }

    const name = this.versionNameDraft.trim() || null;
    if (name === (this.versions.find((v) => v.id === versionId)?.name ?? null)) {
      this.cancelRenameVersion();
      return;
    }

    const token = this.loadToken;
    this.mutatingVersion = true;
    this.api.renameVersion(this.organisationId, versionId, name).subscribe({
      next: (saved) => {
        this.mutatingVersion = false;
        // Another template or organisation since: its versions are not the ones renamed.
        if (token !== this.loadToken) {
          return;
        }

        const savedName = saved ? saved.name ?? null : name;
        this.versions = this.versions.map((v) => (v.id === versionId ? { ...v, name: savedName } : v));
        this.cancelRenameVersion();
      },
      error: () => {
        // Reported by the shared HTTP error toast; the field stays open with what was typed.
        this.mutatingVersion = false;
      }
    });
  }

  askDeleteVersion(version: CommunicationTemplateVersionSummary): void {
    this.confirmingDeleteVersionId = version.id;
  }

  cancelDeleteVersion(): void {
    this.confirmingDeleteVersionId = null;
  }

  /** Hard-deletes a non-active version, then refreshes the list (reselecting the active one if needed). */
  deleteVersion(version: CommunicationTemplateVersionSummary): void {
    if (version.isActive) {
      return;
    }

    this.mutatingVersion = true;
    this.api.deleteVersion(this.organisationId, version.id).subscribe({
      next: () => {
        this.mutatingVersion = false;
        this.confirmingDeleteVersionId = null;

        // The deleted version may be the one we would return to after peeking at the Default.
        if (this.versionBeforeDefault === version.id) {
          this.versionBeforeDefault = null;
        }

        if (this.selectedVersionId === version.id) {
          this.reselectCurrent();
        } else {
          this.loadVersionsForSelection();
        }
      },
      error: () => {
        this.mutatingVersion = false;
      }
    });
  }

  // ---- User-template management (SB-14890) ----

  /** Opens the "New template" modal (Blank | Pre-built), resetting its state. */
  openCreate(): void {
    this.createName = '';
    this.createMode = 'blank';
    this.selectedPrebuiltId = null;
    this.prebuiltTemplates = [];
    this.prebuiltByCategory = [];
    this.prebuiltFilter = '';
    this.creating = false;
    this.modal.open(this.createModal, { size: 'lg', scrollable: true });
  }

  /** Lazily loads the pre-built gallery the first time the user switches to that mode. */
  onCreateModeChange(mode: 'blank' | 'prebuilt'): void {
    this.createMode = mode;
    if (mode === 'prebuilt' && this.prebuiltTemplates.length === 0 && !this.loadingPrebuilt) {
      this.loadingPrebuilt = true;
      this.api.getLibraryGallery(this.organisationId).subscribe({
        next: (templates) => {
          this.prebuiltTemplates = templates ?? [];
          this.rebuildPrebuiltGroups();
          this.loadingPrebuilt = false;
        },
        error: () => {
          this.loadingPrebuilt = false;
        }
      });
    }
  }

  /**
   * The gallery grouped by category, in the order the curator's sort order implies. Grouping by
   * template type would be pointless - there is exactly one entry per type.
   *
   * Recomputed on load and on filter change rather than exposed as a getter. A getter would build new
   * group objects on every change-detection pass, so *ngFor would destroy and recreate all 52 rows
   * continuously - which churns the DOM and, worse, loses clicks: the <li> under the pointer can be
   * replaced between mousedown and click.
   */
  prebuiltByCategory: { category: string; entries: CommunicationLibraryEntrySummary[] }[] = [];

  /** Stable identity for the gallery rows, so filtering reuses the existing nodes. */
  trackPrebuilt(_index: number, entry: CommunicationLibraryEntrySummary): number {
    return entry.id;
  }

  onPrebuiltFilterChange(): void {
    this.rebuildPrebuiltGroups();
  }

  private rebuildPrebuiltGroups(): void {
    const term = this.prebuiltFilter.trim().toLowerCase();
    const matches = (e: CommunicationLibraryEntrySummary) =>
      !term ||
      [e.name, e.description, e.typeTitle, e.emailSubject].some((v) => (v ?? '').toLowerCase().includes(term));

    const groups = new Map<string, CommunicationLibraryEntrySummary[]>();
    for (const entry of this.prebuiltTemplates.filter(matches)) {
      const category = entry.category?.trim() || 'Other';
      const bucket = groups.get(category);
      if (bucket) {
        bucket.push(entry);
      } else {
        groups.set(category, [entry]);
      }
    }

    this.prebuiltByCategory = [...groups.entries()]
      .map(([category, entries]) => ({
        category,
        entries: [...entries].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      }))
      .sort((a, b) => (a.entries[0]?.sortOrder ?? 0) - (b.entries[0]?.sortOrder ?? 0));
  }

  get canCreate(): boolean {
    if (!this.createName.trim() || this.creating) {
      return false;
    }
    return this.createMode === 'blank' || this.selectedPrebuiltId != null;
  }

  /** Creates a blank template or duplicates the chosen pre-built one, then opens it in the editor. */
  confirmCreate(modalRef: { close: () => void }): void {
    if (!this.canCreate) {
      return;
    }

    this.creating = true;
    const name = this.createName.trim();
    const create$ =
      this.createMode === 'prebuilt' && this.selectedPrebuiltId != null
        ? this.api.createUserTemplateFromLibrary(this.organisationId, {
            libraryId: this.selectedPrebuiltId,
            name
          })
        : this.api.createUserTemplate(this.organisationId, this.blankUserRequest(name));

    create$.subscribe({
      next: (created) => {
        this.creating = false;
        modalRef.close();
        // Reselect only after the catalogue (incl. the new row's tags) has refreshed, so the tag
        // editor is derived from the fresh summary rather than the stale pre-create list.
        this.loadCatalogue(created?.id ? () => this.openUserTemplate(created.id) : undefined);
      },
      error: () => {
        this.creating = false;
      }
    });
  }

  /**
   * True when the current selection is a user template that can be cloned (SB-14890).
   *
   * User templates only. A per-type row is no longer a valid duplicate source: SB-15889 narrowed
   * `GetByIdInOrganisationAsync` to user rows, so the server answers 404 for a typed id and the button
   * would sit there doing nothing. Copying stock content is now the gallery's job — "Pre-built" goes
   * through `UserTemplates/FromLibrary`, which has its own visibility predicate.
   */
  get canDuplicate(): boolean {
    if (this.viewingDefault) {
      return false;
    }

    return this.isUserTemplate && this.selectedUserTemplateId != null;
  }

  /**
   * Duplicates the currently selected user template into a new one ("Copy of …") and opens the copy. The
   * copy is made from the SAVED template and opening it leaves this one, so unsaved edits are asked about
   * first (SB-17074).
   */
  duplicateCurrent(): void {
    const sourceId = this.canDuplicate ? this.selectedUserTemplateId : null;
    if (!sourceId || !this.confirmLeavingUnsavedTemplate()) {
      return;
    }

    this.api.duplicateTemplate(this.organisationId, { sourceTemplateId: sourceId }).subscribe({
      next: (created) => {
        this.loadCatalogue(created?.id ? () => this.openUserTemplate(created.id) : undefined);
      },
      // A source saved before the Message text rule is refused with 400 { error: <label key> } (SB-17215).
      error: (err: HttpErrorResponse) => this.showRefusal(err)
    });
  }

  askDeleteTemplate(): void {
    this.confirmingDeleteTemplate = true;
  }

  cancelDeleteTemplate(): void {
    this.confirmingDeleteTemplate = false;
  }

  /**
   * Soft-deletes the selected user template, then refreshes the list and, on the Custom templates tab,
   * puts a blank new template in the editor the way opening that tab does.
   */
  deleteTemplate(): void {
    if (!this.isUserTemplate || this.selectedUserTemplateId == null) {
      return;
    }

    const id = this.selectedUserTemplateId;
    this.api.deleteUserTemplate(this.organisationId, id).subscribe({
      next: () => {
        this.confirmingDeleteTemplate = false;
        if (this.lastUserTemplateId === id) {
          this.lastUserTemplateId = null;
        }
        this.resetSelection();
        this.loadCatalogue(() => this.openNewUserTemplateIfIdle());
      }
    });
  }

  /** Adds a tag from the input box (deferred to the server on the next save/tag change). */
  addTag(): void {
    const tag = this.tagInput.trim();
    if (tag && !this.userTags.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      this.userTags = [...this.userTags, tag];
      this.persistTags();
    }
    this.tagInput = '';
  }

  removeTag(tag: string): void {
    this.userTags = this.userTags.filter((t) => t !== tag);
    this.persistTags();
  }

  /** Persists the current tag set for the selected user template (silent). */
  private persistTags(): void {
    if (!this.isUserTemplate || this.selectedUserTemplateId == null) {
      return;
    }

    const id = this.selectedUserTemplateId;
    this.api.setUserTemplateTags(this.organisationId, id, this.userTags).subscribe({
      next: () => {
        const summary = this.userTemplates.find((t) => t.id === id);
        if (summary) {
          summary.tags = this.userTags.slice();
        }
      }
    });
  }

  private resetSelection(): void {
    this.loadToken++;
    this.selectionKind = null;
    this.selectedTypeId = null;
    this.selectedUserTemplateId = null;
    this.template = undefined;
    this.design = null;
    this.versions = [];
    this.selectedVersionId = null;
    this.confirmingDeleteVersionId = null;
    this.confirmingDeleteTemplate = false;
    this.userTags = [];
    this.resetTemplateDirtyState();
    this.clearDefaultView();
  }

  /** Re-selects the current template (type or user) — used after deleting the open version. */
  private reselectCurrent(): void {
    if (this.isUserTemplate && this.selectedUserTemplateId != null) {
      this.openUserTemplate(this.selectedUserTemplateId);
    } else if (this.selectedTypeId != null) {
      this.openType(this.selectedTypeId);
    }
  }

  /**
   * Loads the type list + the organisation's configured rows + user templates + the merge-tag
   * catalogue. <paramref name="onLoaded"/> runs after the data is in place — used so a just-created
   * template is selected only once its summary (with tags) is present.
   */
  private loadCatalogue(onLoaded?: () => void): void {
    this.loadingCatalogue = true;
    this.error = false;
    const org = this.organisationId;
    forkJoin({
      templates: this.api.getTemplates(org),
      types: this.api.getTypes(org),
      userTemplates: this.api.getUserTemplates(org),
      mergeTags: this.api.getMergeTags(org)
    }).subscribe({
      next: ({ templates, types, userTemplates, mergeTags }) => {
        this.templates = templates ?? [];
        this.types = types ?? [];
        this.userTemplates = userTemplates ?? [];
        this.mergeTags = buildUnlayerMergeTags(mergeTags ?? []);
        this.customTemplateMergeTags = buildUnlayerMergeTags(withoutMessageText(mergeTags ?? []));
        this.rebuildCatalogue();
        this.loadingCatalogue = false;
        onLoaded?.();
      },
      error: () => {
        this.error = true;
        this.loadingCatalogue = false;
      }
    });
  }

  /**
   * Loads the version list for the current selection (type or user template).
   *
   * Selects <paramref name="preferVersionId"/> when it is present in the refreshed list, otherwise the
   * active version. Callers that have just created a version must pass its id: they set
   * {@link selectedVersionId} themselves and this reload would otherwise immediately overwrite it with
   * the active version's id, bouncing the author off the version they just made.
   */
  private loadVersionsForSelection(preferVersionId?: number): void {
    const versions$ = this.isUserTemplate
      ? (this.selectedUserTemplateId == null ? null : this.api.getUserTemplateVersions(this.organisationId, this.selectedUserTemplateId))
      : (this.selectedTypeId == null ? null : this.api.getVersions(this.organisationId, this.selectedTypeId));
    if (!versions$) {
      return;
    }

    const token = this.loadToken;
    this.loadingVersions = true;
    this.versions = [];
    this.cancelRenameVersion(); // the list it was renaming in is being replaced

    // Leave the dropdown showing the Default while its overlay is up — a refresh triggered by a
    // version mutation must not silently swap the selection out from under the read-only pane.
    if (!this.viewingDefault) {
      this.selectedVersionId = null;
    }

    versions$.subscribe({
      next: (versions) => {
        if (token !== this.loadToken) {
          return;
        }

        this.versions = versions ?? [];
        if (!this.viewingDefault) {
          this.selectedVersionId =
            preferVersionId != null && this.versions.some((v) => v.id === preferVersionId)
              ? preferVersionId
              : this.versions.find((v) => v.isActive)?.id ?? null;
        }
        this.loadingVersions = false;
      },
      error: () => {
        if (token === this.loadToken) {
          this.loadingVersions = false;
        }
      }
    });
  }

  /** Loads a user-created template's content into the editor by header id. */
  private loadUserTemplate(id: number): void {
    const token = this.loadToken;
    this.loadingTemplate = true;
    this.template = undefined;
    this.design = null;
    this.api.getUserTemplate(this.organisationId, id).pipe(catchError(() => of(undefined))).subscribe({
      next: (template) => {
        if (token !== this.loadToken) {
          return; // superseded by a newer selection
        }

        if (template) {
          this.template = template;
          this.userTags = this.userTemplates.find((t) => t.id === id)?.tags?.slice() ?? [];
          this.showDesign(this.buildDesign(template));
          this.captureMetaBaseline();
        }
        this.loadingTemplate = false;
      },
      error: () => {
        if (token === this.loadToken) {
          this.loadingTemplate = false;
        }
      }
    });
  }

  /**
   * Builds the create payload for a blank user template.
   *
   * `isOrganisationFooterIncluded` is true: it matches the server-side default for a new header, and the
   * legacy send stack reads this flag in ten places, so a blank template that started with `false` would
   * quietly drop the organisation footer from everything sent with it (SB-15889).
   */
  private blankUserRequest(name: string): SaveUserTemplateRequest {
    return {
      name,
      isUnlayerBased: true,
      isEnabled: true,
      sendToStudents: false,
      sendToParents: true,
      sendToStaff: false,
      isOrganisationFooterIncluded: true,

      // A brand-new template inherits the organisation's Reply-To — the server default, and the only
      // sensible starting point (SB-15933).
      replyToMode: ReplyToMode.Inherit
    };
  }

  /**
   * Loads a catalogue type into the editor, together with its platform Default.
   *
   * The default is fetched for EVERY type, not just uncustomised ones (SB-14889): it backs the
   * always-available read-only "Default" entry in the version dropdown, and having it in memory makes
   * "make an editable copy" a single request. A 404 (no default for this type, or this organisation IS
   * the defaults organisation) is not an error — it just means the entry is not offered.
   */
  private loadTemplate(typeId: number): void {
    const configured = this.templates.some((t) => t.communicationTemplateItemId === typeId);
    const token = this.loadToken;
    this.loadingTemplate = true;
    this.template = undefined;
    this.design = null;

    forkJoin({
      // A configured type that fails to load falls back to a blank canvas, NOT to the default — the org
      // has its own content and silently showing platform content in its place would be misleading.
      own: configured
        ? this.api.getTemplate(this.organisationId, typeId).pipe(catchError(() => of(this.blankTemplate(typeId))))
        : of(null),
      platformDefault: this.api.getDefaultTemplate(this.organisationId, typeId).pipe(catchError(() => of(null)))
    }).subscribe({
      next: ({ own, platformDefault }) => {
        if (token !== this.loadToken) {
          return; // the user moved to another template while this was in flight
        }

        this.defaultTemplate = platformDefault ?? null;

        // Configured ⇒ the org's own content. Otherwise seed from the platform Default (SB-14943 Q4),
        // falling back to a blank canvas when there is no default.
        this.template =
          own ?? (platformDefault ? this.seedFromDefault(platformDefault, typeId) : this.blankTemplate(typeId));
        this.showDesign(this.buildDesign(this.template));
        this.captureMetaBaseline();
        this.loadingTemplate = false;
      },
      error: () => {
        if (token === this.loadToken) {
          this.loadingTemplate = false;
        }
      }
    });
  }


  /**
   * Chooses what to load into the editor so BOTH formats work: an Unlayer template loads its design JSON;
   * a legacy template (HTML body, no design JSON) is wrapped in a single Unlayer HTML block; an empty
   * template starts blank.
   */
  private buildDesign(template: CommunicationTemplate): object | null {
    const design = this.parseDesign(template.designJson);
    if (design) {
      return design;
    }

    if (template.emailBody) {
      return this.htmlToDesign(template.emailBody);
    }

    return null;
  }

  private parseDesign(json?: string): object | null {
    if (!json) {
      return null;
    }

    try {
      return JSON.parse(json) as object;
    } catch {
      return null;
    }
  }

  private htmlToDesign(html: string): object {
    return {
      counters: { u_row: 1, u_column: 1, u_content_html: 1 },
      body: {
        id: 'legacy-body',
        rows: [
          {
            id: 'legacy-row',
            cells: [1],
            columns: [
              {
                id: 'legacy-column',
                contents: [
                  {
                    id: 'legacy-html',
                    type: 'html',
                    values: {
                      html,
                      hideDesktop: false,
                      displayCondition: null,
                      _meta: { htmlID: 'u_content_html_1', htmlClassNames: 'u_content_html' },
                      selectable: true,
                      draggable: true,
                      duplicatable: true,
                      deletable: true,
                      hideable: true
                    }
                  }
                ],
                values: { _meta: { htmlID: 'u_column_1', htmlClassNames: 'u_column' } }
              }
            ],
            values: {
              displayCondition: null,
              columns: false,
              _meta: { htmlID: 'u_row_1', htmlClassNames: 'u_row' },
              selectable: true,
              draggable: true,
              duplicatable: true,
              deletable: true,
              hideable: true
            }
          }
        ],
        values: { _meta: { htmlID: 'u_body', htmlClassNames: 'u_body' } }
      }
    };
  }

  /** Seeds an unconfigured type from the platform Default template (content + flags), as a new org row. */
  private seedFromDefault(defaultTemplate: CommunicationTemplate, typeId: number): CommunicationTemplate {
    return { ...defaultTemplate, id: 0, communicationTemplateItemId: typeId, templateName: String(typeId) };
  }

  /**
   * The empty canvas for a type with neither an organisation row nor a stock entry. Its flags are the
   * ones a header would be created with, so they must match the server's defaults — `false` here used to
   * mean saving an empty canvas turned the organisation footer off (SB-15889).
   */
  private blankTemplate(typeId: number): CommunicationTemplate {
    return {
      id: 0,
      communicationTemplateItemId: typeId,
      templateName: String(typeId),
      isUnlayerBased: true,
      isEnabled: true,
      sendToStudents: false,
      sendToParents: true,
      sendToStaff: false,
      isOrganisationFooterIncluded: true,

      // A brand-new template inherits the organisation's Reply-To — the server default, and the only
      // sensible starting point (SB-15933).
      replyToMode: ReplyToMode.Inherit
    };
  }

  /** The editor model for a new custom template: the flags of {@link blankUserRequest}, nothing else set. */
  private blankUserTemplate(): CommunicationTemplate {
    return {
      ...this.blankUserRequest(''),
      id: 0,
      communicationTemplateItemId: 0,
      templateName: '',
      emailSubject: ''
    };
  }

  /**
   * Builds the version payload from the current editor export + subject/push fields.
   *
   * Carries the header flags too (SB-15889): when this is the first version of a type the organisation
   * has never configured, the server creates the header from this request, and `this.template` at that
   * point is the stock library entry seeded by `seedFromDefault()` — so these are the curator's flags.
   * The server ignores them whenever the header already exists.
   */
  private versionRequest(result: TemplateEditorExport): SaveCommunicationTemplateVersionRequest {
    return {
      emailSubject: this.template?.emailSubject,
      emailBody: result.html,
      pushNotification: this.template?.pushNotification,
      designJson: JSON.stringify(result.design),
      isUnlayerBased: true,
      ...this.headerFlags(this.template)
    };
  }

  /**
   * The five header flags a version-save may have to create the header with. Undefined for a missing
   * source, which the server reads as "keep your previous defaults" rather than as `false`.
   */
  private headerFlags(source?: CommunicationTemplate | null): Partial<SaveCommunicationTemplateVersionRequest> {
    if (!source) {
      return {};
    }

    return {
      isEnabled: source.isEnabled,
      sendToStudents: source.sendToStudents,
      sendToParents: source.sendToParents,
      sendToStaff: source.sendToStaff,
      isOrganisationFooterIncluded: source.isOrganisationFooterIncluded
    };
  }
}
